// src/features/invoices/components/InvoiceDashboard.tsx

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInvoices } from '../hooks/useInvoices';
import { InvoiceLineItemInput, Invoice, Zusatzleistung } from '../types';
import { FileText, Check, Trash2, Plus, Edit3, X, Download, Send, Upload, ChevronDown, FileInput, ChevronLeft, ChevronRight, AlertTriangle, FileDown } from 'lucide-react';
import { payrollPdfAnalyzerService } from '../../../services/payroll/payrollPdfAnalyzerService';
import { payrollDataService } from '../../../services/payroll/payrollDataService';
import { invoiceService } from '../../../services/invoice/invoiceService';
import { lohnjournalImportService, LohnjournalImportErgebnis, LohnjournalStapelFortschritt } from '../../../services/payroll/lohnjournalImportService';
import { lohnjournalSpiegelService, LohnjournalSpiegelTreffer } from '../../../services/payroll/lohnjournalSpiegelService';
import { zusatzleistungenService } from '../../../services/zusatzleistungen/zusatzleistungenService';
import { supabase } from '../../../lib/supabaseClient';
import { useDialog } from '../../../shared/components/DialogProvider';
import { PaymentReconciliationView } from './PaymentReconciliationView';
import { ZusatzleistungenView } from './ZusatzleistungenView';

const GERMAN_MONTHS: Record<number, string> = {
  1: 'Januar', 2: 'Februar', 3: 'März', 4: 'April', 5: 'Mai', 6: 'Juni',
  7: 'Juli', 8: 'August', 9: 'September', 10: 'Oktober', 11: 'November', 12: 'Dezember'
};

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; border: string }> = {
  entwurf: { label: 'Entwurf', color: '#F59E0B', bg: '#FFFBEB', border: '#FDE68A' },
  freigegeben: { label: 'Freigegeben', color: '#3B82F6', bg: '#EFF6FF', border: '#BFDBFE' },
  versendet: { label: 'Versendet', color: '#10B981', bg: '#ECFDF5', border: '#A7F3D0' },
  importiert: { label: 'Importiert', color: '#6B7280', bg: '#F3F4F6', border: '#D1D5DB' }
};

/**
 * Darstellung der Einzelergebnisse im Lohnjournal-Stapelupload
 */
const IMPORT_STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; border: string }> = {
  erfolg: { label: 'Erfolgreich', color: '#059669', bg: '#ECFDF5', border: '#A7F3D0' },
  warnung: { label: 'Teilweise erfolgreich', color: '#D97706', bg: '#FFFBEB', border: '#FDE68A' },
  fehler: { label: 'Fehler', color: '#DC2626', bg: '#FEF2F2', border: '#FECACA' }
};

/**
 * Prüft, ob ein Entwurf mindestens eine Position mit "Lohnabrechnungen" in der Beschreibung hat.
 * Wird für die visuelle Unterscheidung im Badge und die Sortierung (LA-Entwürfe oben) verwendet.
 */
const hatLohnabrechnungen = (inv: { status: string; line_items?: { beschreibung: string }[] }): boolean => {
  if (inv.status !== 'entwurf') return false;
  return (inv.line_items || []).some(li => li.beschreibung.toLowerCase().includes('lohnabrechnungen'));
};

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);
};

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '–';
  return new Date(dateStr).toLocaleDateString('de-DE');
};

/**
 * Datum mit Uhrzeit. Wird für den Zeitpunkt benötigt, zu dem der
 * Dokumentenspiegel eine neuere Fassung des Lohnjournals abgeholt hat. Den
 * Stand, den DATEV zum Dokument liefert, zeigt dagegen formatDate an: DATEV
 * liefert dafür nur ein Datum ohne Uhrzeit.
 */
const formatDateTime = (dateStr: string | null): string => {
  if (!dateStr) return '–';
  return new Date(dateStr).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

// ============================================
// SPLIT-BUTTON: Versenden (primär) + Herunterladen (Dropdown)
// ============================================
interface SplitApproveButtonProps {
  primaryLabel: string;
  primaryLabelBusy: string;
  secondaryLabel: string;
  secondaryLabelBusy: string;
  onPrimary: () => void;
  onSecondary: () => void;
  busyPrimary: boolean;
  busySecondary: boolean;
  disabled?: boolean;
  size?: 'sm' | 'md';
  stopPropagation?: boolean;
}

const SplitApproveButton: React.FC<SplitApproveButtonProps> = ({
  primaryLabel, primaryLabelBusy, secondaryLabel, secondaryLabelBusy,
  onPrimary, onSecondary, busyPrimary, busySecondary, disabled = false,
  size = 'md', stopPropagation = false
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const isBusy = busyPrimary || busySecondary;
  const isDisabled = disabled || isBusy;

  const padding = size === 'sm' ? '6px 12px' : '8px 16px';
  const chevronPadding = size === 'sm' ? '6px 6px' : '8px 8px';
  const fontSize = size === 'sm' ? '13px' : '14px';
  const iconSize = size === 'sm' ? 14 : 16;
  const gap = size === 'sm' ? '4px' : '6px';

  const stopIfNeeded = (e: React.MouseEvent) => {
    if (stopPropagation) e.stopPropagation();
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative', display: 'inline-flex' }} onClick={stopIfNeeded}>
      <button
        onClick={(e) => { stopIfNeeded(e); if (!isDisabled) onPrimary(); }}
        disabled={isDisabled}
        style={{
          padding, backgroundColor: isBusy ? '#9CA3AF' : '#3B82F6',
          color: 'white', border: 'none', borderRadius: '6px 0 0 6px',
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          fontSize, fontWeight: '500', display: 'flex', alignItems: 'center', gap,
          opacity: disabled && !isBusy ? 0.5 : 1
        }}
      >
        <Send size={iconSize} />
        {busyPrimary ? primaryLabelBusy : (busySecondary ? secondaryLabelBusy : primaryLabel)}
      </button>
      <button
        onClick={(e) => { stopIfNeeded(e); if (!isDisabled) setOpen(o => !o); }}
        disabled={isDisabled}
        style={{
          padding: chevronPadding, backgroundColor: isBusy ? '#9CA3AF' : '#3B82F6',
          color: 'white', border: 'none', borderLeft: '1px solid rgba(255,255,255,0.3)',
          borderRadius: '0 6px 6px 0',
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center',
          opacity: disabled && !isBusy ? 0.5 : 1
        }}
        aria-label="Weitere Aktionen"
      >
        <ChevronDown size={iconSize} />
      </button>
      {open && (
        <div
          style={{
            position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 10,
            backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)', minWidth: '220px', overflow: 'hidden'
          }}
        >
          <button
            onClick={(e) => { stopIfNeeded(e); setOpen(false); onSecondary(); }}
            disabled={isDisabled}
            style={{
              width: '100%', padding: '10px 14px', backgroundColor: 'transparent',
              color: '#111827', border: 'none', textAlign: 'left',
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              fontSize: '13px', fontWeight: '500',
              display: 'flex', alignItems: 'center', gap: '8px'
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#F3F4F6'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'; }}
          >
            <Download size={14} />
            {secondaryLabel}
          </button>
        </div>
      )}
    </div>
  );
};

export const InvoiceDashboard: React.FC = () => {
  const {
    invoices,
    loading,
    filter,
    setFilter,
    loadInvoices,
    selectedInvoice,
    selectInvoice,
    closeDetail,
    addLineItem,
    updateLineItem,
    deleteLineItem,
    approveAndSend,
    previewPdf,
    deleteDraft,
    previousInvoice,
    previousInvoiceLoading,
    previousInvoiceLoaded,
    loadPreviousInvoice
  } = useInvoices();

  const navigate = useNavigate();

  // Dialoge laufen über den app-weiten DialogProvider statt über window.confirm()
  // und alert(). Browser-Dialoge werden von Chrome unterdrückt, wenn sie nicht
  // unmittelbar aus einer Nutzergeste entstehen oder das Fenster nicht im
  // Vordergrund liegt; eine Rückfrage liefert dann still false und eine Meldung
  // geht komplett verloren.
  const { confirm: dialogConfirm, notify: dialogNotify } = useDialog();

  const [showAddItem, setShowAddItem] = useState(false);
  const [newItem, setNewItem] = useState<InvoiceLineItemInput>({
    beschreibung: '', menge: 1, einheit: 'Stk', einzelpreis: 0
  });
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editingValues, setEditingValues] = useState<Partial<InvoiceLineItemInput>>({});
  const [approving, setApproving] = useState<string | null>(null);
  const [approvingDownload, setApprovingDownload] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  // Bulk-Versand (nur Entwürfe): Auswahl per Checkbox + Sammelaktion
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkSending, setBulkSending] = useState(false);

  // Sichtbarer Fortschritt des Sammelvorgangs. Die Vor-Prüfung braucht pro
  // Entwurf mehrere Serveraufrufe; ohne Anzeige wirkt der Knopf in dieser Zeit
  // wie tot, obwohl das System arbeitet.
  const [bulkPhase, setBulkPhase] = useState<{ art: 'pruefung' | 'versand'; aktuell: number; gesamt: number } | null>(null);

  // Vor-Versand-Prüfung: offene Zusatzleistungen je Entwurf (Warn-Badges) sowie
  // Lösungsdialog vor dem Versand (Einzel- und Bulk-Pfad).
  const [openZlCounts, setOpenZlCounts] = useState<Record<string, number>>({});
  const [zlPrompt, setZlPrompt] = useState<{
    action: 'send' | 'download';
    mode: 'single' | 'bulk';
    targets: Invoice[];
    affected: { invoiceId: string; label: string; items: Zusatzleistung[] }[];
  } | null>(null);
  const [zlConverting, setZlConverting] = useState<string | null>(null);

  // Manuelle Übernahme in der geöffneten Rechnung: verbliebene offene
  // Zusatzleistungen (z.B. ohne hinterlegten Stundensatz) + laufender Konvert.
  const [detailOpenZl, setDetailOpenZl] = useState<Zusatzleistung[]>([]);
  const [detailZlConverting, setDetailZlConverting] = useState<string | null>(null);

  // Sektion "Letzte Rechnung an diesen Kunden" in der Entwurfs-Detailansicht.
  // Default: eingeklappt. Beim erstmaligen Aufklappen wird die Vorgängerrechnung
  // geladen; der Hook cached das Ergebnis pro selectedInvoice-ID.
  const [showPreviousInvoice, setShowPreviousInvoice] = useState(false);
  const [previousInvoicePdfLoading, setPreviousInvoicePdfLoading] = useState(false);

  // Lohnjournal-Upload State (Stapelverarbeitung in der Rechnungsübersicht)
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'laeuft' | 'fertig'>('idle');
  const [uploadFortschritt, setUploadFortschritt] = useState<LohnjournalStapelFortschritt | null>(null);
  const [uploadErgebnisse, setUploadErgebnisse] = useState<LohnjournalImportErgebnis[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Lohnjournal-Upload State (innerhalb einer geöffneten Rechnung)
  const invoicePayrollFileInputRef = useRef<HTMLInputElement>(null);
  const [invoicePayrollBusy, setInvoicePayrollBusy] = useState(false);
  const [invoicePayrollMessage, setInvoicePayrollMessage] = useState<string | null>(null);
  const [pendingPayrollDecision, setPendingPayrollDecision] = useState<{
    anzahlArbeitnehmer: number;
    month: number;
    year: number;
    customerCompany: string;
    existingCount: number;
    /**
     * Hinweis der Auswahlregel, wenn das PDF mehrere Verarbeitungsläufe
     * enthielt oder die Anzahl unter den gespiegelten Lohnabrechnungen liegt.
     */
    warnung?: string | null;
  } | null>(null);

  // Übernahme aus dem DATEV-Dokumentenspiegel (innerhalb einer geöffneten Rechnung).
  // Wird beim Öffnen eines Entwurfs geprüft: liegt für Kunde und Abrechnungsmonat
  // ein gespiegeltes Lohnjournal vor, kann die Arbeitnehmeranzahl ohne erneuten
  // Upload übernommen werden.
  const [spiegelStatus, setSpiegelStatus] = useState<'idle' | 'pruefung' | 'fertig'>('idle');
  const [spiegelTreffer, setSpiegelTreffer] = useState<LohnjournalSpiegelTreffer | null>(null);
  const [spiegelIstSammelrechnung, setSpiegelIstSammelrechnung] = useState(false);
  const [spiegelPruefFehler, setSpiegelPruefFehler] = useState<string | null>(null);

  // DATEV Export State
  const [showDatevExport, setShowDatevExport] = useState(false);
  const [datevExporting, setDatevExporting] = useState(false);
  const [datevMonth, setDatevMonth] = useState(() => {
    const now = new Date();
    const lastMonth = now.getMonth(); // 0-based, so this is "last month" as 1-based
    return lastMonth === 0 ? 12 : lastMonth;
  });
  const [datevYear, setDatevYear] = useState(() => {
    const now = new Date();
    return now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
  });

  // SEPA-Lastschrift Export State
  // Default-Einzugsdatum: heute + 3 Bankarbeitstage (Sa/So überspringen)
  const computeDefaultColltnDt = (): string => {
    const d = new Date();
    let added = 0;
    while (added < 3) {
      d.setDate(d.getDate() + 1);
      const dow = d.getDay(); // 0 = So, 6 = Sa
      if (dow !== 0 && dow !== 6) added++;
    }
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const [showSepaExport, setShowSepaExport] = useState(false);
  const [sepaExporting, setSepaExporting] = useState(false);
  const [sepaMonth, setSepaMonth] = useState(() => {
    const now = new Date();
    const lastMonth = now.getMonth();
    return lastMonth === 0 ? 12 : lastMonth;
  });
  const [sepaYear, setSepaYear] = useState(() => {
    const now = new Date();
    return now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
  });
  const [sepaReqdColltnDt, setSepaReqdColltnDt] = useState(computeDefaultColltnDt());

  // ============================================
  // VIEW-SWITCHER: Rechnungen verwalten vs. Zahlungsabgleich
  // ============================================
  const [activeView, setActiveView] = useState<'rechnungen' | 'zahlungsabgleich' | 'zusatzleistungen'>('rechnungen');

  const handleViewChange = (view: 'rechnungen' | 'zahlungsabgleich' | 'zusatzleistungen') => {
    setActiveView(view);
    // Offene Picker/Dialoge schliessen, damit keine Restmenüs sichtbar bleiben
    setShowDatevExport(false);
    setShowSepaExport(false);
    setShowUploadModal(false);
  };

  // Zähler für Filter-Buttons
  const counts = {
    alle: invoices.length,
    entwurf: invoices.filter(i => i.status === 'entwurf').length,
    freigegeben: invoices.filter(i => i.status === 'freigegeben').length,
    versendet: invoices.filter(i => i.status === 'versendet').length
  };

  const filteredInvoices = (filter.status 
    ? invoices.filter(i => i.status === filter.status) 
    : invoices
  ).slice().sort((a, b) => {
    // Entwürfe mit Lohnabrechnungen immer oben einsortieren
    const aLA = hatLohnabrechnungen(a) ? 1 : 0;
    const bLA = hatLohnabrechnungen(b) ? 1 : 0;
    return bLA - aLA;
  });

  // Nur im Entwürfe-Tab: Checkbox-Auswahl + Bulk-Versand-Balken einblenden.
  // In allen anderen Tabs bleibt das Karten-Layout unverändert.
  const isEntwurfTab = filter.status === 'entwurf';

  // ============================================
  // MONAT-PICKER HANDLER
  // ============================================
  const navigateMonth = (offset: number) => {
    if (filter.year === undefined || filter.month === undefined) return;
    const newMonth = filter.month + offset;
    let targetMonth = newMonth;
    let targetYear = filter.year;
    if (newMonth < 1) {
      targetMonth = 12;
      targetYear = filter.year - 1;
    } else if (newMonth > 12) {
      targetMonth = 1;
      targetYear = filter.year + 1;
    }
    setFilter({ ...filter, year: targetYear, month: targetMonth });
  };

  const goToVormonat = () => {
    const now = new Date();
    const isJanuary = now.getMonth() === 0;
    setFilter({
      ...filter,
      year: isJanuary ? now.getFullYear() - 1 : now.getFullYear(),
      month: isJanuary ? 12 : now.getMonth()
    });
  };

  const toggleAlleMonate = () => {
    setFilter({ ...filter, year: undefined, month: undefined });
  };

  const isAlleMonate = filter.year === undefined && filter.month === undefined;

  // Beim Wechsel der ausgewählten Rechnung (oder beim Schließen des Detail-Views)
  // den Lohnjournal-Upload-State zurücksetzen, damit Erfolgs-/Fehlermeldungen aus
  // einer vorher angesehenen Rechnung nicht fälschlich in der neuen Detail-Ansicht
  // angezeigt werden.
  // Dependency ist bewusst selectedInvoice?.id (nicht das ganze Objekt): Bei einem
  // Lohnjournal-Upload in derselben Rechnung wird selectInvoice() neu aufgerufen
  // und liefert ein neues Objekt, aber die ID bleibt gleich – die Erfolgsmeldung
  // soll dann sichtbar bleiben.
  useEffect(() => {
    setInvoicePayrollMessage(null);
    setPendingPayrollDecision(null);
    setInvoicePayrollBusy(false);
    setShowPreviousInvoice(false);
  }, [selectedInvoice?.id]);

  // Verfügbarkeit des gespiegelten Lohnjournals prüfen.
  // Die Einzelwerte werden bewusst in Konstanten gezogen, damit die
  // Dependency-Liste exakt das abbildet, was im Effekt verwendet wird
  // (CI=true lässt keine ESLint-Warnung durchgehen).
  const spiegelInvoiceId = selectedInvoice?.id ?? null;
  const spiegelInvoiceStatus = selectedInvoice?.status ?? null;
  const spiegelCustomerId = selectedInvoice?.customer_id ?? null;
  const spiegelYear = selectedInvoice?.year ?? null;
  const spiegelMonth = selectedInvoice?.month ?? null;

  useEffect(() => {
    if (!spiegelInvoiceId || spiegelInvoiceStatus !== 'entwurf' || !spiegelCustomerId || !spiegelYear || !spiegelMonth) {
      setSpiegelStatus('idle');
      setSpiegelTreffer(null);
      setSpiegelIstSammelrechnung(false);
      setSpiegelPruefFehler(null);
      return;
    }

    let abgebrochen = false;
    setSpiegelStatus('pruefung');
    setSpiegelTreffer(null);
    setSpiegelPruefFehler(null);

    (async () => {
      const ergebnis = await lohnjournalSpiegelService.pruefeVerfuegbarkeit({
        customerId: spiegelCustomerId,
        year: spiegelYear,
        month: spiegelMonth
      });
      if (abgebrochen) return;
      setSpiegelIstSammelrechnung(ergebnis.istSammelrechnung);
      setSpiegelTreffer(ergebnis.treffer);
      setSpiegelPruefFehler(ergebnis.fehler);
      setSpiegelStatus('fertig');
    })();

    return () => { abgebrochen = true; };
  }, [spiegelInvoiceId, spiegelInvoiceStatus, spiegelCustomerId, spiegelYear, spiegelMonth]);

  // Auswahl für den Bulk-Versand leeren, sobald Monat oder Filter-Tab wechselt,
  // damit nie versehentlich veraltete IDs versendet werden.
  useEffect(() => {
    setSelectedIds([]);
  }, [filter.status, filter.year, filter.month]);

  // Offene Zusatzleistungen je Entwurf für die Warn-Badges laden. Match auf
  // Leistungsmonat/-jahr der Rechnung inkl. Sammelrechnungs-Scope (Service).
  useEffect(() => {
    const drafts = invoices.filter(i => i.status === 'entwurf');
    if (drafts.length === 0) {
      setOpenZlCounts({});
      return;
    }
    let cancelled = false;
    (async () => {
      const counts = await zusatzleistungenService.getOpenCountsForInvoices(
        drafts.map(d => ({ id: d.id, customer_id: d.customer_id, year: d.year, month: d.month }))
      );
      if (!cancelled) setOpenZlCounts(counts);
    })();
    return () => { cancelled = true; };
  }, [invoices]);

  const performSingleSend = async (invoiceId: string, skipConfirm = false) => {
    if (!skipConfirm) {
      const bestaetigt = await dialogConfirm({
        titel: 'Rechnung versenden',
        text: 'Rechnung freigeben und per E-Mail versenden?',
        bestaetigenLabel: 'Freigeben & Versenden'
      });
      if (!bestaetigt) return;
    }
    setApproving(invoiceId);
    try {
      const result = await approveAndSend(invoiceId);
      if (result.success) {
        if (result.emailSent) {
          await dialogNotify({
            titel: 'Rechnung versendet',
            text: `Rechnung ${result.rechnungsnummer} wurde freigegeben und an ${result.versendetAn} versendet.`
          });
        } else {
          await dialogNotify({
            titel: 'E-Mail-Versand fehlgeschlagen',
            text: `Rechnung ${result.rechnungsnummer} wurde freigegeben, aber der E-Mail-Versand ist fehlgeschlagen. Bitte manuell versenden.`,
            variante: 'warnung'
          });
        }
      } else {
        await dialogNotify({
          titel: 'Versand fehlgeschlagen',
          text: result.error || 'Unbekannter Fehler',
          variante: 'gefahr'
        });
      }
    } finally {
      setApproving(null);
    }
  };

  // Öffnet eine Rechnung und zieht bei Entwürfen automatisch offene
  // Zusatzleistungen als Positionen – aber nur, wenn beim abrechnenden Kunden
  // (bei Unterkunden der Auftraggeber) ein Stundensatz hinterlegt ist.
  // Verbleibende offene Leistungen (z.B. ohne hinterlegten Satz) landen im
  // manuellen Übernahme-Block der Detailansicht.
  const handleOpenInvoice = async (inv: Invoice) => {
    await selectInvoice(inv.id);
    if (inv.status === 'entwurf' && inv.customer_id) {
      const res = await zusatzleistungenService.autoConvertOpenForInvoice({
        id: inv.id, customer_id: inv.customer_id, status: inv.status, year: inv.year, month: inv.month
      });
      if (res.convertedCount > 0) {
        await selectInvoice(inv.id);
        await loadInvoices();
      }
      const remaining = await zusatzleistungenService.getOpenForInvoice({
        customer_id: inv.customer_id, year: inv.year, month: inv.month
      });
      setDetailOpenZl(remaining);
    } else {
      setDetailOpenZl([]);
    }
  };

  const handleApprove = async (invoiceId: string) => {
    // Vor-Versand-Prüfung: offene Zusatzleistungen (nur bei Entwürfen). Bei
    // Treffern öffnet sich der Lösungsdialog und der Versand pausiert.
    const target = (selectedInvoice && selectedInvoice.id === invoiceId)
      ? selectedInvoice
      : invoices.find(i => i.id === invoiceId) || null;
    if (target && target.status === 'entwurf') {
      let opened = false;
      try {
        opened = await zlGateBeforeSend('send', 'single', [target]);
      } catch (error) {
        await dialogNotify({
          titel: 'Prüfung fehlgeschlagen',
          text: `Die Prüfung auf offene Zusatzleistungen ist fehlgeschlagen. Es wurde nichts versendet.\n\n${error instanceof Error ? error.message : 'Unbekannter Fehler'}`,
          variante: 'gefahr'
        });
        return;
      }
      if (opened) return;
    }
    await performSingleSend(invoiceId);
  };

  const performApproveAndDownload = async (invoiceId: string, skipConfirm = false) => {
    if (!skipConfirm) {
      const bestaetigt = await dialogConfirm({
        titel: 'Rechnung herunterladen',
        text: 'Rechnung freigeben und als PDF herunterladen (ohne E-Mail-Versand)?',
        bestaetigenLabel: 'Freigeben & Herunterladen'
      });
      if (!bestaetigt) return;
    }
    setApprovingDownload(invoiceId);
    try {
      const result = await invoiceService.approveAndDownload(invoiceId);
      if (result.success) {
        await dialogNotify({
          titel: 'Rechnung freigegeben',
          text: `Rechnung ${result.rechnungsnummer} wurde freigegeben und heruntergeladen.`
        });
      } else {
        await dialogNotify({
          titel: 'Freigabe fehlgeschlagen',
          text: result.error || 'Unbekannter Fehler',
          variante: 'gefahr'
        });
      }
    } finally {
      setApprovingDownload(null);
    }
  };

  const handleApproveAndDownload = async (invoiceId: string) => {
    // Gleiche Vor-Prüfung wie beim Versand: offene Zusatzleistungen (nur bei
    // Entwürfen). So kann auch über den Herunterladen-Weg nichts still verloren
    // gehen.
    const target = (selectedInvoice && selectedInvoice.id === invoiceId)
      ? selectedInvoice
      : invoices.find(i => i.id === invoiceId) || null;
    if (target && target.status === 'entwurf') {
      let opened = false;
      try {
        opened = await zlGateBeforeSend('download', 'single', [target]);
      } catch (error) {
        await dialogNotify({
          titel: 'Prüfung fehlgeschlagen',
          text: `Die Prüfung auf offene Zusatzleistungen ist fehlgeschlagen. Es wurde nichts freigegeben.\n\n${error instanceof Error ? error.message : 'Unbekannter Fehler'}`,
          variante: 'gefahr'
        });
        return;
      }
      if (opened) return;
    }
    await performApproveAndDownload(invoiceId);
  };

  const handleDelete = async (invoiceId: string) => {
    const bestaetigt = await dialogConfirm({
      titel: 'Entwurf löschen',
      text: 'Rechnungsentwurf wirklich löschen?',
      bestaetigenLabel: 'Löschen',
      variante: 'gefahr'
    });
    if (!bestaetigt) return;
    setDeleting(invoiceId);
    try {
      await deleteDraft(invoiceId);
    } finally {
      setDeleting(null);
    }
  };

  // ============================================
  // BULK-VERSAND (nur Entwürfe)
  // ============================================

  const toggleSelect = (invoiceId: string) => {
    setSelectedIds(prev =>
      prev.includes(invoiceId) ? prev.filter(id => id !== invoiceId) : [...prev, invoiceId]
    );
  };

  const handleBulkApproveAndSend = async () => {
    // Nur Entwürfe, die aktuell in der gefilterten Liste vorhanden sind, werden
    // versendet. Das schützt gegen veraltete IDs in der Auswahl.
    const targets = filteredInvoices.filter(inv => selectedIds.includes(inv.id));
    if (targets.length === 0) return;

    // Vor-Versand-Prüfung: offene Zusatzleistungen. Bei Treffern öffnet sich der
    // Lösungsdialog und der Bulk-Versand pausiert. Die Prüfung braucht pro
    // Entwurf mehrere Serveraufrufe, deshalb läuft sie mit sichtbarem
    // Fortschritt und abgesichert gegen Fehler: Ein Serverfehler darf die Kette
    // nicht mehr still abreißen lassen.
    let opened = false;
    setBulkPhase({ art: 'pruefung', aktuell: 0, gesamt: targets.length });
    try {
      opened = await zlGateBeforeSend('send', 'bulk', targets, (aktuell, gesamt) => {
        setBulkPhase({ art: 'pruefung', aktuell, gesamt });
      });
    } catch (error) {
      await dialogNotify({
        titel: 'Prüfung fehlgeschlagen',
        text: `Die Prüfung auf offene Zusatzleistungen ist fehlgeschlagen. Es wurde nichts versendet.\n\n${error instanceof Error ? error.message : 'Unbekannter Fehler'}`,
        variante: 'gefahr'
      });
      return;
    } finally {
      setBulkPhase(null);
    }
    if (opened) return;

    await performBulkSend(targets);
  };

  const performBulkSend = async (targets: Invoice[], skipConfirm = false) => {
    if (targets.length === 0) return;

    if (!skipConfirm) {
      const bestaetigt = await dialogConfirm({
        titel: 'Sammelversand bestätigen',
        text: `${targets.length} Rechnungsentwurf/-entwürfe freigeben und per E-Mail versenden?`,
        bestaetigenLabel: 'Freigeben & Versenden'
      });
      if (!bestaetigt) return;
    }

    // Ergebnis-Zusammenfassung wird ausserhalb des try/finally gehalten und erst
    // nach dem Zurücksetzen der Busy-Zustände angezeigt.
    let summaryTitel = 'Sammelversand abgeschlossen';
    let summaryText = '';
    let summaryVariante: 'info' | 'warnung' | 'gefahr' = 'info';

    setBulkSending(true);
    setBulkPhase({ art: 'versand', aktuell: 0, gesamt: targets.length });
    try {
      const results: {
        label: string;
        success: boolean;
        emailSent?: boolean;
        rechnungsnummer?: string;
        error?: string;
      }[] = [];

      // Sequenziell versenden: exakt derselbe Pfad wie der Einzelversand
      // (Validierung, Renumbering, Rechnungsnummernvergabe, PDF, E-Mail –
      // alles server-seitig in approveAndSend). Ein einziger Reload danach.
      let index = 0;
      for (const inv of targets) {
        index++;
        setBulkPhase({ art: 'versand', aktuell: index, gesamt: targets.length });
        const label = inv.customer?.company || inv.empfaenger_firma;
        try {
          const result = await invoiceService.approveAndSend(inv.id);
          results.push({
            label,
            success: result.success,
            emailSent: result.emailSent,
            rechnungsnummer: result.rechnungsnummer,
            error: result.error
          });
        } catch (error) {
          results.push({
            label,
            success: false,
            error: error instanceof Error ? error.message : 'Unbekannter Fehler'
          });
        }
      }

      // Ein einziger Reload nach allen Versänden, danach Auswahl leeren
      await loadInvoices();
      setSelectedIds([]);

      // Ergebnis-Zusammenfassung (analog zur DATEV-Skipped-Auflistung)
      const erfolgreich = results.filter(r => r.success);
      const emailFehlgeschlagen = erfolgreich.filter(r => r.emailSent === false);
      const fehlgeschlagen = results.filter(r => !r.success);

      summaryText = `${erfolgreich.length} von ${results.length} Rechnung(en) freigegeben und versendet.`;

      if (emailFehlgeschlagen.length > 0) {
        summaryText += `\n\n${emailFehlgeschlagen.length} freigegeben, aber E-Mail-Versand fehlgeschlagen (bitte manuell versenden):\n` +
          emailFehlgeschlagen
            .map(r => `- ${r.label}${r.rechnungsnummer ? ` (${r.rechnungsnummer})` : ''}`)
            .join('\n');
      }

      if (fehlgeschlagen.length > 0) {
        summaryText += `\n\n${fehlgeschlagen.length} fehlgeschlagen:\n` +
          fehlgeschlagen.map(r => `- ${r.label}: ${r.error || 'Unbekannter Fehler'}`).join('\n');
      }

      if (fehlgeschlagen.length > 0) {
        summaryTitel = 'Sammelversand mit Fehlern';
        summaryVariante = 'gefahr';
      } else if (emailFehlgeschlagen.length > 0) {
        summaryTitel = 'Sammelversand mit Hinweisen';
        summaryVariante = 'warnung';
      }
    } catch (error) {
      summaryTitel = 'Sammelversand abgebrochen';
      summaryVariante = 'gefahr';
      summaryText = `${summaryText ? summaryText + '\n\n' : ''}${error instanceof Error ? error.message : 'Unbekannter Fehler'}`;
    } finally {
      setBulkPhase(null);
      setBulkSending(false);
    }

    if (summaryText) {
      await dialogNotify({ titel: summaryTitel, text: summaryText, variante: summaryVariante });
    }
  };

  // ============================================
  // VOR-VERSAND-PRÜFUNG: offene Zusatzleistungen
  // ============================================

  // Prüft die Ziel-Entwürfe auf offene Zusatzleistungen (Match auf Leistungsmonat
  // der Rechnung inkl. Sammelrechnungs-Scope). Öffnet bei Treffern den Lösungs-
  // dialog und gibt true zurück (Versand pausiert), sonst false.
  // onFortschritt meldet den Bearbeitungsstand an die Oberfläche.
  const zlGateBeforeSend = async (
    action: 'send' | 'download',
    mode: 'single' | 'bulk',
    targets: Invoice[],
    onFortschritt?: (aktuell: number, gesamt: number) => void
  ): Promise<boolean> => {
    const affected: { invoiceId: string; label: string; items: Zusatzleistung[] }[] = [];
    let index = 0;
    for (const t of targets) {
      index++;
      if (onFortschritt) onFortschritt(index, targets.length);
      if (t.status !== 'entwurf') continue;
      // Automatische Übernahme (nur bei hinterlegtem Stundensatz) vor der Prüfung –
      // so bleiben nur Leistungen ohne hinterlegten Satz für die manuelle Auswahl.
      await zusatzleistungenService.autoConvertOpenForInvoice({
        id: t.id, customer_id: t.customer_id, status: t.status, year: t.year, month: t.month
      });
      const items = await zusatzleistungenService.getOpenForInvoice({
        customer_id: t.customer_id, year: t.year, month: t.month
      });
      if (items.length > 0) {
        affected.push({ invoiceId: t.id, label: t.customer?.company || t.empfaenger_firma, items });
      }
    }
    if (affected.length === 0) return false;
    setZlPrompt({ action, mode, targets, affected });
    return true;
  };

  // Übernimmt eine einzelne offene Zusatzleistung als Position in ihren Entwurf.
  const zlConvertOne = async (invoiceId: string, item: Zusatzleistung) => {
    setZlConverting(item.id);
    try {
      const res = await zusatzleistungenService.convertToInvoice(item.id, invoiceId);
      if (!res.success) {
        await dialogNotify({
          titel: 'Übernahme fehlgeschlagen',
          text: res.error || 'Übernahme fehlgeschlagen',
          variante: 'gefahr'
        });
        return;
      }
      setZlPrompt(prev => {
        if (!prev) return prev;
        const affected = prev.affected
          .map(a => a.invoiceId === invoiceId ? { ...a, items: a.items.filter(i => i.id !== item.id) } : a)
          .filter(a => a.items.length > 0);
        return { ...prev, affected };
      });
      if (selectedInvoice?.id === invoiceId) await selectInvoice(invoiceId);
      await loadInvoices();
    } finally {
      setZlConverting(null);
    }
  };

  // Übernimmt alle offenen Zusatzleistungen eines Entwurfs.
  const zlConvertAllForInvoice = async (invoiceId: string) => {
    const block = zlPrompt?.affected.find(a => a.invoiceId === invoiceId);
    const target = zlPrompt?.targets.find(t => t.id === invoiceId);
    if (!block) return;
    for (const item of [...block.items]) {
      setZlConverting(item.id);
      const res = await zusatzleistungenService.convertToInvoice(item.id, invoiceId);
      if (!res.success) {
        await dialogNotify({
          titel: 'Übernahme fehlgeschlagen',
          text: res.error || 'Übernahme fehlgeschlagen',
          variante: 'gefahr'
        });
        break;
      }
    }
    setZlConverting(null);
    // Verbleibende offene Leistungen frisch nachladen (robust bei Teilfehlern)
    if (target) {
      const remaining = await zusatzleistungenService.getOpenForInvoice({
        customer_id: target.customer_id, year: target.year, month: target.month
      });
      setZlPrompt(prev => prev ? {
        ...prev,
        affected: prev.affected
          .map(a => a.invoiceId === invoiceId ? { ...a, items: remaining } : a)
          .filter(a => a.items.length > 0)
      } : prev);
    }
    if (selectedInvoice?.id === invoiceId) await selectInvoice(invoiceId);
    await loadInvoices();
  };

  // "Trotzdem/Jetzt" aus dem Dialog: schließt den Dialog und führt die
  // ursprünglich anvisierte Aktion aus (Versand oder Herunterladen; Bestätigung
  // entfällt – bewusst gewählt).
  const zlPromptProceed = async () => {
    if (!zlPrompt) return;
    const { action, mode, targets } = zlPrompt;
    setZlPrompt(null);
    if (action === 'download') {
      await performApproveAndDownload(targets[0].id, true);
    } else if (mode === 'single') {
      await performSingleSend(targets[0].id, true);
    } else {
      await performBulkSend(targets, true);
    }
  };

  // Manuelle Übernahme aus dem Block der geöffneten Rechnung (einzeln).
  const detailConvertOne = async (item: Zusatzleistung) => {
    if (!selectedInvoice) return;
    setDetailZlConverting(item.id);
    try {
      const res = await zusatzleistungenService.convertToInvoice(item.id, selectedInvoice.id);
      if (!res.success) {
        await dialogNotify({
          titel: 'Übernahme fehlgeschlagen',
          text: res.error || 'Übernahme fehlgeschlagen',
          variante: 'gefahr'
        });
        return;
      }
      setDetailOpenZl(prev => prev.filter(i => i.id !== item.id));
      await selectInvoice(selectedInvoice.id);
      await loadInvoices();
    } finally {
      setDetailZlConverting(null);
    }
  };

  // Manuelle Übernahme aus dem Block der geöffneten Rechnung (alle).
  const detailConvertAll = async () => {
    if (!selectedInvoice) return;
    const items = [...detailOpenZl];
    for (const item of items) {
      setDetailZlConverting(item.id);
      const res = await zusatzleistungenService.convertToInvoice(item.id, selectedInvoice.id);
      if (!res.success) {
        await dialogNotify({
          titel: 'Übernahme fehlgeschlagen',
          text: res.error || 'Übernahme fehlgeschlagen',
          variante: 'gefahr'
        });
        break;
      }
    }
    setDetailZlConverting(null);
    const remaining = await zusatzleistungenService.getOpenForInvoice({
      customer_id: selectedInvoice.customer_id, year: selectedInvoice.year, month: selectedInvoice.month
    });
    setDetailOpenZl(remaining);
    await selectInvoice(selectedInvoice.id);
    await loadInvoices();
  };

  const handleAddItem = async () => {
    if (!selectedInvoice || !newItem.beschreibung || newItem.einzelpreis <= 0) return;
    await addLineItem(selectedInvoice.id, newItem);
    setNewItem({ beschreibung: '', menge: 1, einheit: 'Stk', einzelpreis: 0 });
    setShowAddItem(false);
  };

  const handleSaveEdit = async (itemId: string) => {
    if (!selectedInvoice) return;
    await updateLineItem(itemId, editingValues);
    setEditingItemId(null);
    setEditingValues({});
  };

  const handleDeleteItem = async (itemId: string) => {
    if (!selectedInvoice) return;
    await deleteLineItem(itemId, selectedInvoice.id);
  };

  // ============================================
  // LOHNJOURNAL-UPLOAD INNERHALB EINER RECHNUNG
  // ============================================

  /**
   * Wird aufgerufen, wenn der Nutzer im Positionsblock einer geöffneten Rechnung
   * ein Lohnjournal-PDF auswählt. Validiert Kunde + Monat/Jahr gegen die
   * aktuell geöffnete Rechnung. Bei Mismatch: harter Fehler.
   * Bei vorhandenen Positionen: Dialog mit drei Optionen (append/replace/abbrechen).
   */
  const handleInvoicePayrollUpload = async (file: File) => {
    if (!selectedInvoice) return;

    // Guard: nur Entwürfe dürfen ergänzt werden
    if (selectedInvoice.status !== 'entwurf') {
      setInvoicePayrollMessage('Nur Entwürfe können ergänzt werden. Diese Rechnung ist bereits freigegeben oder versendet.');
      return;
    }

    setInvoicePayrollBusy(true);
    setInvoicePayrollMessage('Lohnjournal wird analysiert...');

    try {
      // 1) PDF analysieren
      const analysisResult = await payrollPdfAnalyzerService.analyzeAndIdentify(file);
      if (!analysisResult.success || !analysisResult.data) {
        setInvoicePayrollMessage(analysisResult.error || 'PDF-Analyse fehlgeschlagen');
        setInvoicePayrollBusy(false);
        return;
      }

      const { beraternummer, mandantennummer, month, year, anzahlArbeitnehmer, warnung } = analysisResult.data;

      // 2) Kunde aus Lohnjournal matchen
      const { data: matchedCustomer, error: customerError } = await supabase
        .from('customers')
        .select('id, company')
        .eq('beraternummer', beraternummer)
        .eq('mandantennummer', mandantennummer)
        .eq('deleted', false)
        .maybeSingle();

      if (customerError || !matchedCustomer) {
        setInvoicePayrollMessage(`Kein Kunde mit Beraternummer ${beraternummer} und Mandantennummer ${mandantennummer} gefunden.`);
        setInvoicePayrollBusy(false);
        return;
      }

      // 3) Validierungs-Gate: Kunde muss zur Rechnung passen
      if (matchedCustomer.id !== selectedInvoice.customer_id) {
        setInvoicePayrollMessage(
          `Lohnjournal gehört zu Kunde "${matchedCustomer.company}" – passt nicht zur aktuellen Rechnung von "${selectedInvoice.empfaenger_firma}".`
        );
        setInvoicePayrollBusy(false);
        return;
      }

      // 4) Validierungs-Gate: Monat/Jahr muss zur Rechnung passen
      if (month !== selectedInvoice.month || year !== selectedInvoice.year) {
        setInvoicePayrollMessage(
          `Lohnjournal gehört zu ${GERMAN_MONTHS[month]} ${year} – passt nicht zum Leistungszeitraum dieser Rechnung (${GERMAN_MONTHS[selectedInvoice.month]} ${selectedInvoice.year}).`
        );
        setInvoicePayrollBusy(false);
        return;
      }

      // 5) Validierungs-Gate: Arbeitnehmeranzahl muss extrahiert worden sein
      if (!anzahlArbeitnehmer || anzahlArbeitnehmer <= 0) {
        setInvoicePayrollMessage('Arbeitnehmeranzahl konnte nicht aus dem Lohnjournal extrahiert werden.');
        setInvoicePayrollBusy(false);
        return;
      }

      // 6) Payroll-Daten speichern (analog zum Übersicht-Upload)
      const saveResult = await payrollDataService.savePayrollData({
        customerId: matchedCustomer.id,
        year,
        month,
        anzahlArbeitnehmer,
        anzahlArbeitnehmerMitEntgelt: analysisResult.data.anzahlArbeitnehmerMitEntgelt,
        hasNextMonthBeitragsnachweis: analysisResult.data.hasNextMonthBeitragsnachweis,
        nextMonth: analysisResult.data.nextMonth,
        nextMonthYear: analysisResult.data.nextMonthYear,
        sourceFilename: file.name
      });

      if (!saveResult.success) {
        setInvoicePayrollMessage(`Fehler beim Speichern der Abrechnungsdaten: ${saveResult.error}`);
        setInvoicePayrollBusy(false);
        return;
      }

      // 7) Entscheidung treffen: leer → direkt einfügen, sonst Dialog öffnen
      const existingCount = (selectedInvoice.line_items || []).length;

      if (existingCount === 0) {
        // Keine bestehenden Positionen → direkt als erste Position einfügen
        const result = await invoiceService.populateDraftFromPayroll(
          selectedInvoice.id,
          anzahlArbeitnehmer,
          { mode: 'append' }
        );
        if (result.success) {
          const grundmeldung = `Position für ${matchedCustomer.company} (${anzahlArbeitnehmer} AN) wurde ergänzt.`;
          setInvoicePayrollMessage(warnung ? `${grundmeldung} Hinweis: ${warnung}` : grundmeldung);
          await selectInvoice(selectedInvoice.id);
        } else {
          setInvoicePayrollMessage(result.error || 'Position konnte nicht eingefügt werden');
        }
        setInvoicePayrollBusy(false);
      } else {
        // Bestehende Positionen → Drei-Wege-Dialog öffnen
        setPendingPayrollDecision({
          anzahlArbeitnehmer,
          month,
          year,
          customerCompany: matchedCustomer.company,
          existingCount,
          warnung
        });
        setInvoicePayrollMessage(null);
        setInvoicePayrollBusy(false);
      }

    } catch (error) {
      console.error('[InvoicePayrollUpload] Fehler:', error);
      setInvoicePayrollMessage(error instanceof Error ? error.message : 'Unbekannter Fehler');
      setInvoicePayrollBusy(false);
    } finally {
      // File-Input zurücksetzen, damit derselbe File nochmal ausgewählt werden kann
      if (invoicePayrollFileInputRef.current) {
        invoicePayrollFileInputRef.current.value = '';
      }
    }
  };

  // ============================================
  // ÜBERNAHME AUS DEM DATEV-DOKUMENTENSPIEGEL
  // ============================================

  /**
   * Übernimmt die Arbeitnehmeranzahl aus dem bereits gespiegelten DATEV-Lohnjournal,
   * ohne dass die Datei erneut hochgeladen werden muss.
   *
   * Der Weg ist bewusst identisch zum Upload aufgebaut: das PDF wird aus dem
   * Storage geholt, durch dieselbe Analysekette geschickt und über dieselben
   * Gates geprüft. Die Gates sind hier Sicherheitsnetz statt Zuordnungshilfe –
   * Kunde und Monat stehen durch die Spiegel-Abfrage bereits fest, ein im
   * Spiegel falsch abgelegtes Dokument darf aber trotzdem nicht durchrutschen.
   *
   * Der Upload-Pfad bleibt vollständig erhalten und wird von dieser Funktion
   * nicht berührt.
   */
  const handleSpiegelUebernahme = async () => {
    if (!selectedInvoice || !spiegelTreffer) return;

    // Guard: nur Entwürfe dürfen ergänzt werden
    if (selectedInvoice.status !== 'entwurf') {
      setInvoicePayrollMessage('Nur Entwürfe können ergänzt werden. Diese Rechnung ist bereits freigegeben oder versendet.');
      return;
    }

    setInvoicePayrollBusy(true);
    setInvoicePayrollMessage('Lohnjournal wird aus dem Dokumentenspiegel geladen...');

    try {
      // 1) PDF aus dem Storage holen
      const ladeErgebnis = await lohnjournalSpiegelService.ladeLohnjournalDatei(spiegelTreffer);
      if (!ladeErgebnis.success || !ladeErgebnis.datei) {
        setInvoicePayrollMessage(ladeErgebnis.error || 'Das Lohnjournal konnte nicht geladen werden.');
        setInvoicePayrollBusy(false);
        return;
      }

      // 2) PDF analysieren (identische Kette wie beim Upload)
      setInvoicePayrollMessage('Lohnjournal wird analysiert...');
      const analysisResult = await payrollPdfAnalyzerService.analyzeAndIdentify(ladeErgebnis.datei);
      if (!analysisResult.success || !analysisResult.data) {
        setInvoicePayrollMessage(analysisResult.error || 'PDF-Analyse fehlgeschlagen');
        setInvoicePayrollBusy(false);
        return;
      }

      const { beraternummer, mandantennummer, month, year, anzahlArbeitnehmer, warnung } = analysisResult.data;

      // 3) Kunde aus dem Lohnjournal matchen
      const { data: matchedCustomer, error: customerError } = await supabase
        .from('customers')
        .select('id, company')
        .eq('beraternummer', beraternummer)
        .eq('mandantennummer', mandantennummer)
        .eq('deleted', false)
        .maybeSingle();

      if (customerError || !matchedCustomer) {
        setInvoicePayrollMessage(`Kein Kunde mit Beraternummer ${beraternummer} und Mandantennummer ${mandantennummer} gefunden.`);
        setInvoicePayrollBusy(false);
        return;
      }

      // 4) Sicherheitsnetz: Kunde muss zur Rechnung passen
      if (matchedCustomer.id !== selectedInvoice.customer_id) {
        setInvoicePayrollMessage(
          `Das Lohnjournal im Dokumentenspiegel gehört zu Kunde "${matchedCustomer.company}" – passt nicht zur aktuellen Rechnung von "${selectedInvoice.empfaenger_firma}". Es wurde nichts übernommen.`
        );
        setInvoicePayrollBusy(false);
        return;
      }

      // 5) Sicherheitsnetz: Monat/Jahr muss zur Rechnung passen
      if (month !== selectedInvoice.month || year !== selectedInvoice.year) {
        setInvoicePayrollMessage(
          `Das Lohnjournal im Dokumentenspiegel gehört zu ${GERMAN_MONTHS[month]} ${year} – passt nicht zum Leistungszeitraum dieser Rechnung (${GERMAN_MONTHS[selectedInvoice.month]} ${selectedInvoice.year}). Es wurde nichts übernommen.`
        );
        setInvoicePayrollBusy(false);
        return;
      }

      // 6) Sicherheitsnetz: Arbeitnehmeranzahl muss extrahiert worden sein
      if (!anzahlArbeitnehmer || anzahlArbeitnehmer <= 0) {
        setInvoicePayrollMessage('Arbeitnehmeranzahl konnte nicht aus dem Lohnjournal extrahiert werden.');
        setInvoicePayrollBusy(false);
        return;
      }

      // 7) Payroll-Daten speichern (identisch zum Upload-Pfad)
      const saveResult = await payrollDataService.savePayrollData({
        customerId: matchedCustomer.id,
        year,
        month,
        anzahlArbeitnehmer,
        anzahlArbeitnehmerMitEntgelt: analysisResult.data.anzahlArbeitnehmerMitEntgelt,
        hasNextMonthBeitragsnachweis: analysisResult.data.hasNextMonthBeitragsnachweis,
        nextMonth: analysisResult.data.nextMonth,
        nextMonthYear: analysisResult.data.nextMonthYear,
        sourceFilename: ladeErgebnis.datei.name
      });

      if (!saveResult.success) {
        setInvoicePayrollMessage(`Fehler beim Speichern der Abrechnungsdaten: ${saveResult.error}`);
        setInvoicePayrollBusy(false);
        return;
      }

      // 8) Entscheidung treffen: leer → direkt einfügen, sonst Drei-Wege-Dialog
      const existingCount = (selectedInvoice.line_items || []).length;

      if (existingCount === 0) {
        const result = await invoiceService.populateDraftFromPayroll(
          selectedInvoice.id,
          anzahlArbeitnehmer,
          { mode: 'append' }
        );
        if (result.success) {
          const grundmeldung = `Position für ${matchedCustomer.company} (${anzahlArbeitnehmer} AN) wurde aus dem Dokumentenspiegel übernommen.`;
          setInvoicePayrollMessage(warnung ? `${grundmeldung} Hinweis: ${warnung}` : grundmeldung);
          await selectInvoice(selectedInvoice.id);
        } else {
          setInvoicePayrollMessage(result.error || 'Position konnte nicht eingefügt werden');
        }
        setInvoicePayrollBusy(false);
      } else {
        setPendingPayrollDecision({
          anzahlArbeitnehmer,
          month,
          year,
          customerCompany: matchedCustomer.company,
          existingCount,
          warnung
        });
        setInvoicePayrollMessage(null);
        setInvoicePayrollBusy(false);
      }

    } catch (error) {
      console.error('[InvoiceSpiegelUebernahme] Fehler:', error);
      setInvoicePayrollMessage(error instanceof Error ? error.message : 'Unbekannter Fehler');
      setInvoicePayrollBusy(false);
    }
  };

  /**
   * Wird aufgerufen, nachdem der Nutzer im Drei-Wege-Dialog "Zusätzlich" oder "Ersetzen" gewählt hat.
   */
  const handlePayrollDecision = async (mode: 'append' | 'replace') => {
    if (!selectedInvoice || !pendingPayrollDecision) return;
    const { anzahlArbeitnehmer, warnung } = pendingPayrollDecision;

    setInvoicePayrollBusy(true);
    setInvoicePayrollMessage(mode === 'replace' ? 'Bestehende Positionen werden ersetzt...' : 'Position wird angehängt...');

    try {
      const result = await invoiceService.populateDraftFromPayroll(
        selectedInvoice.id,
        anzahlArbeitnehmer,
        { mode }
      );
      if (result.success) {
        const grundmeldung = mode === 'replace'
          ? `Bestehende Positionen wurden ersetzt (${anzahlArbeitnehmer} AN).`
          : `Position wurde zusätzlich übernommen (${anzahlArbeitnehmer} AN).`;
        setInvoicePayrollMessage(warnung ? `${grundmeldung} Hinweis: ${warnung}` : grundmeldung);
        await selectInvoice(selectedInvoice.id);
      } else {
        setInvoicePayrollMessage(result.error || 'Position konnte nicht eingefügt werden');
      }
    } catch (error) {
      console.error('[InvoicePayrollUpload] Fehler bei Entscheidung:', error);
      setInvoicePayrollMessage(error instanceof Error ? error.message : 'Unbekannter Fehler');
    } finally {
      setPendingPayrollDecision(null);
      setInvoicePayrollBusy(false);
    }
  };

  const handlePayrollDecisionCancel = () => {
    setPendingPayrollDecision(null);
    setInvoicePayrollMessage(null);
  };

  const handleDownloadPdf = async (invoiceId: string) => {
    setDownloading(true);
    try {
      const result = await previewPdf(invoiceId);
      if (!result.success) {
        await dialogNotify({
          titel: 'PDF-Vorschau fehlgeschlagen',
          text: result.error || 'Unbekannter Fehler',
          variante: 'gefahr'
        });
      }
    } finally {
      setDownloading(false);
    }
  };

  /**
   * Toggle der ausklappbaren "Letzte Rechnung"-Sektion in der Entwurfs-Detailansicht.
   * Beim erstmaligen Aufklappen wird die Vorgängerrechnung über den Hook geladen.
   */
  const handleTogglePreviousInvoice = async () => {
    const opening = !showPreviousInvoice;
    setShowPreviousInvoice(opening);
    if (opening) {
      await loadPreviousInvoice();
    }
  };

  /**
   * Öffnet das PDF der bereits festgeschriebenen Vorgängerrechnung im neuen Tab
   * (gleicher Weg wie die normale PDF-Vorschau).
   */
  const handleDownloadPreviousInvoicePdf = async (invoiceId: string) => {
    setPreviousInvoicePdfLoading(true);
    try {
      const result = await previewPdf(invoiceId);
      if (!result.success) {
        await dialogNotify({
          titel: 'PDF-Vorschau fehlgeschlagen',
          text: result.error || 'Unbekannter Fehler',
          variante: 'gefahr'
        });
      }
    } finally {
      setPreviousInvoicePdfLoading(false);
    }
  };

  // ============================================
  // LOHNJOURNAL UPLOAD (Stapelverarbeitung)
  // ============================================

  /**
   * Wandelt eine Dateiauswahl in ein Array um.
   *
   * nurPdf wird beim Drag-and-Drop gesetzt, damit versehentlich mitgezogene
   * Fremddateien nicht in die Verarbeitung geraten. Über den Dateidialog werden
   * alle ausgewählten Dateien übernommen, fehlerhafte Dateien landen dann mit
   * einer Fehlermeldung in der Ergebnisliste.
   */
  const sammleDateien = (liste: FileList | null, nurPdf: boolean): File[] => {
    const dateien: File[] = [];
    if (!liste) return dateien;
    for (let i = 0; i < liste.length; i++) {
      const datei = liste[i];
      if (!datei) continue;
      if (nurPdf && datei.type !== 'application/pdf') continue;
      dateien.push(datei);
    }
    return dateien;
  };

  /**
   * Verarbeitet eine oder mehrere Lohnjournal-PDFs nacheinander.
   *
   * Die Verarbeitungskette (PDF analysieren, Kunde matchen, Abrechnungsdaten
   * speichern, Rechnungsentwurf anlegen) liegt im lohnjournalImportService.
   * Eine fehlerhafte Datei stoppt den Stapel nicht, sie wird im Ergebnis
   * ausgewiesen und die Verarbeitung läuft weiter.
   */
  const handleLohnjournalUpload = async (files: File[]) => {
    if (files.length === 0) return;

    setUploadStatus('laeuft');
    setUploadErgebnisse([]);
    setUploadFortschritt({
      aktuelleDatei: 1,
      gesamtDateien: files.length,
      dateiname: files[0].name,
      phase: 'Verarbeitung wird gestartet...'
    });

    const ergebnisse = await lohnjournalImportService.verarbeiteStapel(files, {
      onFortschritt: (fortschritt) => setUploadFortschritt(fortschritt),
      onErgebnis: (ergebnis) => setUploadErgebnisse(bisher => bisher.concat([ergebnis]))
    });

    setUploadErgebnisse(ergebnisse);
    setUploadFortschritt(null);
    setUploadStatus('fertig');

    // Rechnungsliste einmal nach Abschluss des gesamten Stapels neu laden
    await loadInvoices();

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const resetUploadModal = () => {
    setShowUploadModal(false);
    setUploadStatus('idle');
    setUploadFortschritt(null);
    setUploadErgebnisse([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /**
   * Setzt das Modal für einen weiteren Stapel zurück, ohne es zu schließen.
   */
  const starteWeiterenUpload = () => {
    setUploadStatus('idle');
    setUploadFortschritt(null);
    setUploadErgebnisse([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /**
   * Rendert das Ergebnis einer einzelnen Datei in der Ergebnisliste.
   */
  const renderImportErgebnis = (ergebnis: LohnjournalImportErgebnis, index: number) => {
    const config = IMPORT_STATUS_CONFIG[ergebnis.status] || IMPORT_STATUS_CONFIG.fehler;

    return (
      <div
        key={`${ergebnis.dateiname}-${index}`}
        style={{
          padding: '10px 12px',
          backgroundColor: config.bg,
          border: `1px solid ${config.border}`,
          borderRadius: '6px',
          marginBottom: '8px'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
          <div style={{ flexShrink: 0, marginTop: '1px' }}>
            {ergebnis.status === 'erfolg' && <Check size={16} color={config.color} />}
            {ergebnis.status === 'warnung' && <AlertTriangle size={16} color={config.color} />}
            {ergebnis.status === 'fehler' && <X size={16} color={config.color} />}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: '13px', fontWeight: '600', color: config.color,
              wordBreak: 'break-word', marginBottom: '2px'
            }}>
              {ergebnis.dateiname}
            </div>
            <div style={{ fontSize: '12px', color: '#374151', lineHeight: '1.5' }}>
              {ergebnis.meldung}
            </div>
            {ergebnis.hinweise.map((hinweis, hinweisIndex) => (
              <div
                key={hinweisIndex}
                style={{
                  fontSize: '12px', color: '#92400E', lineHeight: '1.5',
                  marginTop: '6px', display: 'flex', alignItems: 'flex-start', gap: '6px'
                }}
              >
                <AlertTriangle size={13} color="#D97706" style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>{hinweis}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // DATEV Export Handler
  const handleDatevExport = async () => {
    setDatevExporting(true);
    try {
      const result = await invoiceService.generateDatevExport(datevYear, datevMonth);
      if (result.success) {
        // Periodenhinweis: zeigt, ob nur der gewählte Monat oder zusätzlich
        // nachgezogene ältere Monate enthalten sind. Buchhaltungsmonat in DATEV
        // bleibt immer der gewählte Monat.
        const periods = result.periods || [];
        const bookingMonth = `${datevMonth.toString().padStart(2, '0')}/${datevYear}`;
        let periodInfo = '';
        if (periods.length > 1) {
          const formatted = periods
            .map(p => {
              const [y, m] = p.split('-');
              return `${GERMAN_MONTHS[Number(m)]} ${y}`;
            })
            .join(', ');
          periodInfo = `\n\nEnthaltene Belegmonate: ${formatted}\nDATEV-Buchungsmonat: ${bookingMonth}`;
        } else if (periods.length === 1) {
          const [y, m] = periods[0].split('-');
          periodInfo = `\n\nEnthaltener Belegmonat: ${GERMAN_MONTHS[Number(m)]} ${y}`;
        }
        // Schutznetz: Rechnungen ohne DATEV-Debitorennummer wurden NICHT exportiert.
        // Sie werden sichtbar aufgelistet, damit sie nicht stillschweigend verloren gehen.
        let skippedInfo = '';
        if (result.skippedCount && result.skippedCount > 0) {
          const liste = (result.skipped || [])
            .map(s => `- ${s.rechnungsnummer} (${s.firma})`)
            .join('\n');
          skippedInfo = `\n\nACHTUNG: ${result.skippedCount} Rechnung(en) wurden NICHT exportiert (keine DATEV-Debitorennummer):\n${liste}\n\nBitte die DATEV-Debitorennummer im Kundenstamm ergänzen und anschließend erneut exportieren.`;
        }
        setShowDatevExport(false);
        await dialogNotify({
          titel: 'DATEV Export erfolgreich',
          text: `${result.invoiceCount} Buchungszeile(n) exportiert.${periodInfo}${skippedInfo}`,
          variante: (result.skippedCount && result.skippedCount > 0) ? 'warnung' : 'info'
        });
      } else {
        await dialogNotify({
          titel: 'DATEV Export fehlgeschlagen',
          text: result.error || 'Unbekannter Fehler',
          variante: 'gefahr'
        });
      }
    } catch (error) {
      await dialogNotify({
        titel: 'DATEV Export Fehler',
        text: error instanceof Error ? error.message : 'Unbekannter Fehler',
        variante: 'gefahr'
      });
    } finally {
      setDatevExporting(false);
    }
  };

  // SEPA-Lastschrift Export Handler
  const handleSepaExport = async () => {
    setSepaExporting(true);
    try {
      const result = await invoiceService.generateSepaXml(sepaYear, sepaMonth, sepaReqdColltnDt);
      if (result.success) {
        setShowSepaExport(false);
        await dialogNotify({
          titel: 'SEPA-Lastschrift erfolgreich',
          text: `${result.invoiceCount} Lastschriften über ${result.ctrlSum} EUR exportiert.`
        });
      } else {
        // Hard-Fail: Validierungsfehler komplett anzeigen
        if (result.validationErrors && result.validationErrors.length > 0) {
          await dialogNotify({
            titel: 'SEPA-Export abgebrochen',
            text: 'Validierungsfehler:\n\n' + result.validationErrors.map((e, i) => `${i + 1}. ${e}`).join('\n'),
            variante: 'gefahr'
          });
        } else {
          await dialogNotify({
            titel: 'SEPA-Export fehlgeschlagen',
            text: result.error || 'Unbekannter Fehler',
            variante: 'gefahr'
          });
        }
      }
    } catch (error) {
      await dialogNotify({
        titel: 'SEPA-Export Fehler',
        text: error instanceof Error ? error.message : 'Unbekannter Fehler',
        variante: 'gefahr'
      });
    } finally {
      setSepaExporting(false);
    }
  };

  // ============================================
  // LÖSUNGSDIALOG: offene Zusatzleistungen vor Versand
  // (als Variable definiert, damit er in Detail- UND Listenansicht erscheint)
  // ============================================
  const zlRemainingAffected = zlPrompt ? zlPrompt.affected.reduce((s, a) => s + a.items.length, 0) : 0;
  const zlActionVerb = zlPrompt?.action === 'download' ? 'herunterladen' : 'versenden';
  const zlPromptModal = zlPrompt ? (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1200
    }}>
      <div style={{
        backgroundColor: 'white', borderRadius: '12px', padding: '24px',
        maxWidth: '640px', width: '92%', maxHeight: '85vh', overflowY: 'auto',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
      }}>
        <h2 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: '#111827' }}>
          Offene Zusatzleistungen
        </h2>
        <p style={{ fontSize: '14px', color: '#374151', lineHeight: '1.6', margin: '0 0 20px 0' }}>
          {zlRemainingAffected > 0
            ? `Für die folgenden Rechnungen sind noch nicht abgerechnete Zusatzleistungen erfasst. Du kannst sie jetzt in die Rechnung übernehmen oder bewusst ohne sie ${zlActionVerb}.`
            : `Alle Zusatzleistungen wurden übernommen. Du kannst jetzt ${zlActionVerb}.`}
        </p>

        {zlPrompt.affected.map(block => (
          <div key={block.invoiceId} style={{
            border: '1px solid #E5E7EB', borderRadius: '8px', marginBottom: '12px', overflow: 'hidden'
          }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '10px 14px', backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB'
            }}>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>{block.label}</span>
              <button
                onClick={() => zlConvertAllForInvoice(block.invoiceId)}
                disabled={zlConverting !== null}
                style={{
                  padding: '4px 10px', backgroundColor: '#EFF6FF', color: '#2563EB',
                  border: '1px solid #BFDBFE', borderRadius: '6px',
                  cursor: zlConverting !== null ? 'not-allowed' : 'pointer',
                  fontSize: '13px', fontWeight: '500'
                }}
              >
                Alle übernehmen
              </button>
            </div>
            {block.items.map(item => (
              <div key={item.id} style={{
                display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '12px', alignItems: 'center',
                padding: '10px 14px', borderBottom: '1px solid #F3F4F6'
              }}>
                <div>
                  <div style={{ fontSize: '13px', color: '#111827', whiteSpace: 'pre-wrap' }}>{item.beschreibung}</div>
                  <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>
                    {item.art === 'stunden' && item.stunden !== null
                      ? `${String(Number(item.stunden)).replace('.', ',')} Std${item.stundensatz !== null ? ` à ${formatCurrency(Number(item.stundensatz))}` : ''}`
                      : 'Festbetrag'}
                  </div>
                </div>
                <div style={{
                  fontSize: '13px', fontWeight: '600', textAlign: 'right', whiteSpace: 'nowrap',
                  color: Number(item.betrag) < 0 ? '#DC2626' : '#111827'
                }}>
                  {formatCurrency(Number(item.betrag))}
                </div>
                <button
                  onClick={() => zlConvertOne(block.invoiceId, item)}
                  disabled={zlConverting !== null}
                  style={{
                    padding: '4px 10px', backgroundColor: '#F0FDF4', color: '#16A34A',
                    border: '1px solid #BBF7D0', borderRadius: '6px',
                    cursor: zlConverting !== null ? 'not-allowed' : 'pointer',
                    fontSize: '13px', fontWeight: '500', whiteSpace: 'nowrap'
                  }}
                >
                  {zlConverting === item.id ? 'Übernehme...' : 'In Rechnung übernehmen'}
                </button>
              </div>
            ))}
          </div>
        ))}

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button
            onClick={() => setZlPrompt(null)}
            disabled={zlConverting !== null}
            style={{
              padding: '8px 16px', backgroundColor: '#F3F4F6', color: '#374151',
              border: 'none', borderRadius: '6px',
              cursor: zlConverting !== null ? 'not-allowed' : 'pointer',
              fontSize: '14px', fontWeight: '500'
            }}
          >
            Abbrechen
          </button>
          <button
            onClick={zlPromptProceed}
            disabled={zlConverting !== null}
            style={{
              padding: '8px 16px',
              backgroundColor: zlRemainingAffected > 0 ? '#F59E0B' : '#3B82F6',
              color: 'white', border: 'none', borderRadius: '6px',
              cursor: zlConverting !== null ? 'not-allowed' : 'pointer',
              fontSize: '14px', fontWeight: '500'
            }}
          >
            {zlRemainingAffected > 0
              ? (zlPrompt.action === 'download' ? 'Trotzdem herunterladen' : 'Trotzdem versenden')
              : (zlPrompt.action === 'download' ? 'Jetzt herunterladen' : 'Jetzt versenden')}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  // ============================================
  // DETAIL VIEW
  // ============================================
  if (selectedInvoice) {
    const inv = selectedInvoice;
    const statusCfg = STATUS_CONFIG[inv.status];
    const isEditable = inv.status === 'entwurf';
    const lineItems = inv.line_items || [];
    const emailEmpfaenger = inv.customer?.rechnungs_email || inv.customer?.email || null;
    const detailIstEntwurfMitLA = hatLohnabrechnungen(inv);
    const detailBadgeLabel = detailIstEntwurfMitLA ? 'Entwurf mit LA' : statusCfg.label;
    const detailBadgeBg = detailIstEntwurfMitLA ? '#ECFDF5' : statusCfg.bg;
    const detailBadgeColor = detailIstEntwurfMitLA ? '#059669' : statusCfg.color;
    const detailBadgeBorder = detailIstEntwurfMitLA ? '#A7F3D0' : statusCfg.border;

    // ============================================
    // ÜBERNAHME AUS DEM DOKUMENTENSPIEGEL: Anzeigezustand
    // ============================================
    // Bei Sammelrechnungen wird die Schaltfläche gar nicht erst gerendert, weil
    // das Lohnjournal dort fachlich beim Unterkunden liegt und eine Zuordnung
    // zum Auftraggeber nicht eindeutig wäre. In allen anderen Fällen ist sie
    // sichtbar und nur dann klickbar, wenn wirklich ein Journal vorliegt.
    const spiegelKnopfSichtbar = isEditable && !spiegelIstSammelrechnung;
    const spiegelPruefungLaeuft = spiegelStatus !== 'fertig';
    const spiegelUebernahmeMoeglich = spiegelStatus === 'fertig' && spiegelTreffer !== null && !invoicePayrollBusy;

    let spiegelHinweis: string | null = null;
    let spiegelHinweisBg = '#F9FAFB';
    let spiegelHinweisColor = '#6B7280';

    if (spiegelKnopfSichtbar) {
      if (spiegelPruefungLaeuft) {
        spiegelHinweis = 'Der Dokumentenspiegel wird geprüft...';
      } else if (spiegelPruefFehler) {
        spiegelHinweis = `${spiegelPruefFehler} Die Übernahme steht deshalb nicht zur Verfügung, der Upload bleibt möglich.`;
        spiegelHinweisBg = '#FEF2F2';
        spiegelHinweisColor = '#991B1B';
      } else if (!spiegelTreffer) {
        spiegelHinweis = `Für ${GERMAN_MONTHS[inv.month]} ${inv.year} liegt kein Lohnjournal im Dokumentenspiegel. Die Übernahme steht zur Verfügung, sobald DATEV das Journal geliefert hat.`;
      } else if (spiegelTreffer.ersetztAm) {
        // replaced_at markiert NICHT, dass die vorliegende Fassung veraltet ist.
        // Es ist der Zeitpunkt, zu dem DATEV die Vorgaengerfassung durch genau
        // diese Fassung ersetzt hat. Die Vorgaengerversion liegt im Archiv.
        spiegelHinweis = `Lohnjournal im Dokumentenspiegel vorhanden, Stand ${formatDate(spiegelTreffer.datevZeitstempel)}. DATEV hat am ${formatDateTime(spiegelTreffer.ersetztAm)} eine neuere Fassung geliefert, diese liegt hier vor. Die vorherige Fassung ist archiviert.`;
        spiegelHinweisBg = '#FFFBEB';
        spiegelHinweisColor = '#92400E';
      } else {
        spiegelHinweis = `Lohnjournal im Dokumentenspiegel vorhanden, Stand ${formatDate(spiegelTreffer.datevZeitstempel)}.`;
      }
    }

    return (
      <div>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              onClick={closeDetail}
              style={{
                padding: '8px 16px', backgroundColor: '#F3F4F6', border: 'none',
                borderRadius: '6px', cursor: 'pointer', fontSize: '14px', color: '#374151'
              }}
            >
              ← Zurück
            </button>
            <h2 style={{ fontSize: '20px', fontWeight: '600', margin: 0, color: '#111827' }}>
              {inv.rechnungsnummer || 'Entwurf'} – {inv.empfaenger_firma}
            </h2>
            <span style={{
              padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500',
              backgroundColor: detailBadgeBg, color: detailBadgeColor, border: `1px solid ${detailBadgeBorder}`
            }}>
              {detailBadgeLabel}
            </span>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {/* Download-Button: Immer sichtbar */}
            <button
              onClick={() => handleDownloadPdf(inv.id)}
              disabled={downloading || lineItems.length === 0}
              style={{
                padding: '8px 16px', backgroundColor: '#F3F4F6', color: '#374151',
                border: '1px solid #D1D5DB', borderRadius: '6px',
                cursor: downloading || lineItems.length === 0 ? 'not-allowed' : 'pointer',
                fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px',
                opacity: lineItems.length === 0 ? 0.5 : 1
              }}
            >
              <Download size={16} />
              {downloading ? 'Wird generiert...' : 'PDF Vorschau'}
            </button>

            {/* Entwurf: Freigeben & Versenden (Split) + Löschen */}
            {isEditable && (
              <>
                <SplitApproveButton
                  primaryLabel="Freigeben & Versenden"
                  primaryLabelBusy="Wird versendet..."
                  secondaryLabel="Freigeben & Herunterladen"
                  secondaryLabelBusy="Wird generiert..."
                  onPrimary={() => handleApprove(inv.id)}
                  onSecondary={() => handleApproveAndDownload(inv.id)}
                  busyPrimary={approving === inv.id}
                  busySecondary={approvingDownload === inv.id}
                  disabled={lineItems.length === 0}
                  size="md"
                />
                <button
                  onClick={() => handleDelete(inv.id)}
                  disabled={deleting === inv.id}
                  style={{
                    padding: '8px 16px', backgroundColor: '#FEE2E2', color: '#DC2626',
                    border: 'none', borderRadius: '6px', cursor: 'pointer',
                    fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px'
                  }}
                >
                  <Trash2 size={16} />
                  Löschen
                </button>
              </>
            )}

            {/* Freigegeben: Versenden (Split) + Herunterladen-Option */}
            {inv.status === 'freigegeben' && (
              <SplitApproveButton
                primaryLabel="Versenden"
                primaryLabelBusy="Wird versendet..."
                secondaryLabel="Herunterladen"
                secondaryLabelBusy="Wird generiert..."
                onPrimary={() => handleApprove(inv.id)}
                onSecondary={() => handleApproveAndDownload(inv.id)}
                busyPrimary={approving === inv.id}
                busySecondary={approvingDownload === inv.id}
                size="md"
              />
            )}

            {/* Versendet: Info */}
            {inv.status === 'versendet' && inv.versendet_an && (
              <div style={{
                padding: '8px 16px', backgroundColor: '#ECFDF5', color: '#059669',
                borderRadius: '6px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px',
                border: '1px solid #A7F3D0'
              }}>
                <Check size={14} />
                Versendet an {inv.versendet_an}
              </div>
            )}
          </div>
        </div>

        {/* USt-Konsistenz-Warnung: Kunde hat reverse_charge=true, Rechnung hat aber ust_satz > 0.
            Das passiert, wenn der Kunde NACH Entwurfserstellung auf Reverse Charge umgestellt wurde.
            Wird beim Versand durch send-invoice.js automatisch korrigiert. */}
        {inv.status !== 'versendet' && inv.customer?.reverse_charge === true && Number(inv.ust_satz) > 0 && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#FFFBEB',
            border: '1px solid #FDE68A',
            borderRadius: '8px',
            marginBottom: '24px',
            fontSize: '13px',
            color: '#92400E',
            lineHeight: '1.5'
          }}>
            <strong>Hinweis: USt-Satz passt nicht zur Kundeneinstellung.</strong>{' '}
            Der Kunde ist als Reverse-Charge-Kunde konfiguriert (USt-IdNr: {inv.customer?.ust_id_kunde || '–'}),
            diese Rechnung weist jedoch {Number(inv.ust_satz)}% Umsatzsteuer aus.
            Beim Versand werden Beträge automatisch neu berechnet: USt-Satz auf 0% gesetzt,
            Bruttobetrag = Nettobetrag ({formatCurrency(Number(inv.netto_betrag))}).
          </div>
        )}

        {/* Rechnungsinfo */}
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px'
        }}>
          {/* Empfänger */}
          <div style={{
            padding: '16px', backgroundColor: '#F9FAFB', borderRadius: '8px', border: '1px solid #E5E7EB'
          }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#6B7280', marginBottom: '8px', textTransform: 'uppercase' }}>
              Empfänger
            </div>
            <div style={{ fontSize: '14px', color: '#111827', lineHeight: '1.6' }}>
              <div style={{ fontWeight: '600' }}>{inv.empfaenger_firma}</div>
              {inv.empfaenger_anrede && inv.empfaenger_nachname && (
                <div>{inv.empfaenger_anrede} {inv.empfaenger_vorname} {inv.empfaenger_nachname}</div>
              )}
              {inv.empfaenger_strasse && <div>{inv.empfaenger_strasse}</div>}
              {inv.empfaenger_plz && inv.empfaenger_ort && (
                <div>{inv.empfaenger_plz} {inv.empfaenger_ort}</div>
              )}
              {inv.empfaenger_land && inv.empfaenger_land !== 'Deutschland' && (
                <div>{inv.empfaenger_land}</div>
              )}
            </div>
          </div>

          {/* Metadaten */}
          <div style={{
            padding: '16px', backgroundColor: '#F9FAFB', borderRadius: '8px', border: '1px solid #E5E7EB'
          }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#6B7280', marginBottom: '8px', textTransform: 'uppercase' }}>
              Rechnungsdaten
            </div>
            <div style={{ fontSize: '14px', color: '#111827', display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 16px' }}>
              {inv.rechnungsnummer && (
                <><span style={{ color: '#6B7280' }}>Rechnungs-Nr.</span><span style={{ fontWeight: '500' }}>{inv.rechnungsnummer}</span></>
              )}
              <span style={{ color: '#6B7280' }}>Rechnungsdatum</span>
              <span>{inv.rechnungsdatum ? formatDate(inv.rechnungsdatum) : 'Bei Freigabe'}</span>
              <span style={{ color: '#6B7280' }}>Leistungszeitraum</span>
              <span>{formatDate(inv.leistungszeitraum_von)} – {formatDate(inv.leistungszeitraum_bis)}</span>
              {inv.empfaenger_kundennummer && (
                <><span style={{ color: '#6B7280' }}>Kundennummer</span><span>{inv.empfaenger_kundennummer}</span></>
              )}
              {inv.ansprechpartner_name && (
                <><span style={{ color: '#6B7280' }}>Ansprechpartner</span><span>{inv.ansprechpartner_name}</span></>
              )}
              {emailEmpfaenger && (
                <><span style={{ color: '#6B7280' }}>E-Mail-Empfänger</span><span>{emailEmpfaenger}</span></>
              )}
            </div>
          </div>
        </div>

        {/* Positionen */}
        <div style={{
          backgroundColor: 'white', borderRadius: '8px', border: '1px solid #E5E7EB', overflow: 'hidden'
        }}>
          <div style={{
            padding: '12px 16px', backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
          }}>
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>Positionen</span>
            {isEditable && (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {spiegelKnopfSichtbar && (
                  <button
                    onClick={handleSpiegelUebernahme}
                    disabled={!spiegelUebernahmeMoeglich}
                    title={
                      spiegelPruefungLaeuft
                        ? 'Es wird geprüft, ob ein Lohnjournal im Dokumentenspiegel liegt.'
                        : (spiegelTreffer
                            ? 'Übernimmt die Arbeitnehmeranzahl aus dem gespiegelten DATEV-Lohnjournal.'
                            : `Für ${GERMAN_MONTHS[inv.month]} ${inv.year} liegt kein Lohnjournal im Dokumentenspiegel.`)
                    }
                    style={{
                      padding: '4px 12px', backgroundColor: '#FAF5FF', color: '#7E22CE',
                      border: '1px solid #E9D5FF', borderRadius: '6px',
                      cursor: spiegelUebernahmeMoeglich ? 'pointer' : 'not-allowed',
                      fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px',
                      opacity: spiegelUebernahmeMoeglich ? 1 : 0.5
                    }}
                  >
                    <FileDown size={14} />
                    {spiegelPruefungLaeuft ? 'Dokumentenspiegel wird geprüft...' : 'Aus Lohnjournal übernehmen'}
                  </button>
                )}
                <button
                  onClick={() => invoicePayrollFileInputRef.current?.click()}
                  disabled={invoicePayrollBusy}
                  style={{
                    padding: '4px 12px', backgroundColor: '#F0FDF4', color: '#16A34A',
                    border: '1px solid #BBF7D0', borderRadius: '6px',
                    cursor: invoicePayrollBusy ? 'not-allowed' : 'pointer',
                    fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px',
                    opacity: invoicePayrollBusy ? 0.6 : 1
                  }}
                >
                  <Upload size={14} /> {invoicePayrollBusy ? 'Wird verarbeitet...' : 'Lohnjournal hochladen'}
                </button>
                <input
                  ref={invoicePayrollFileInputRef}
                  type="file"
                  accept=".pdf"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file && file.type === 'application/pdf') {
                      handleInvoicePayrollUpload(file);
                    }
                  }}
                />
                <button
                  onClick={async () => {
                    if (!showAddItem && lineItems.length === 0) {
                      // Vorausfüllung bei leerer Rechnung
                      const beschreibung = `Lohnabrechnungen ${String(inv.month).padStart(2, '0')}/${inv.year}`;
                      let einzelpreis = 0;
                      const pricing = inv.customer_id ? await invoiceService.getCurrentPricing(inv.customer_id, { year: inv.year, month: inv.month }) : null;
                      if (pricing) {
                        einzelpreis = Number(pricing.preis_pro_arbeitnehmer);
                      }
                      setNewItem({ beschreibung, menge: 1, einheit: 'Stk', einzelpreis });
                    }
                    setShowAddItem(!showAddItem);
                  }}
                  style={{
                    padding: '4px 12px', backgroundColor: '#EFF6FF', color: '#3B82F6',
                    border: '1px solid #BFDBFE', borderRadius: '6px', cursor: 'pointer',
                    fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px'
                  }}
                >
                  <Plus size={14} /> Position hinzufügen
                </button>
              </div>
            )}
          </div>

          {/* Zustand des Dokumentenspiegels für diesen Kunden und Abrechnungsmonat */}
          {spiegelHinweis && (
            <div style={{
              padding: '8px 16px', fontSize: '12px', lineHeight: '1.5',
              backgroundColor: spiegelHinweisBg, color: spiegelHinweisColor,
              borderBottom: '1px solid #E5E7EB'
            }}>
              {spiegelHinweis}
            </div>
          )}

          {/* Status-Meldung des Lohnjournal-Uploads */}
          {invoicePayrollMessage && (() => {
            // Dreistufige Einfärbung: Fehler rot, Erfolg mit Hinweis der
            // Auswahlregel gelb, glatter Erfolg grün. Ein Hinweis darf nicht
            // als unauffälliger Erfolg durchgehen.
            const meldungKlein = invoicePayrollMessage.toLowerCase();
            const istFehler = meldungKlein.includes('fehler')
              || meldungKlein.includes('passt nicht')
              || meldungKlein.includes('nicht gefunden')
              || meldungKlein.includes('konnte nicht');
            const istHinweis = !istFehler && meldungKlein.includes('hinweis:');

            const meldungBg = istFehler ? '#FEF2F2' : (istHinweis ? '#FFFBEB' : '#F0FDF4');
            const meldungColor = istFehler ? '#991B1B' : (istHinweis ? '#92400E' : '#166534');

            return (
              <div style={{
                padding: '10px 16px', fontSize: '13px',
                backgroundColor: meldungBg,
                color: meldungColor,
                borderBottom: '1px solid #E5E7EB',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
              }}>
                <span>{invoicePayrollMessage}</span>
                <button
                  onClick={() => setInvoicePayrollMessage(null)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: 'inherit' }}
                >
                  <X size={14} />
                </button>
              </div>
            );
          })()}

          {/* Tabellen-Header */}
          <div style={{
            display: 'grid', gridTemplateColumns: '40px 1fr 100px 100px 120px 60px',
            padding: '8px 16px', fontSize: '12px', fontWeight: '600', color: '#6B7280',
            borderBottom: '1px solid #E5E7EB', backgroundColor: '#FAFAFA'
          }}>
            <span>Pos.</span>
            <span>Beschreibung</span>
            <span style={{ textAlign: 'right' }}>Menge</span>
            <span style={{ textAlign: 'right' }}>Einzelpreis</span>
            <span style={{ textAlign: 'right' }}>Gesamtpreis</span>
            <span></span>
          </div>

          {/* Positionen */}
          {lineItems.sort((a, b) => a.position - b.position).map(item => (
            <div key={item.id} style={{
              display: 'grid', gridTemplateColumns: '40px 1fr 100px 100px 120px 60px',
              padding: '10px 16px', borderBottom: '1px solid #F3F4F6', alignItems: 'center',
              fontSize: '14px', color: '#111827'
            }}>
              {editingItemId === item.id ? (
                // Edit-Modus
                <>
                  <span>{item.position}.</span>
                  <input
                    value={editingValues.beschreibung ?? item.beschreibung}
                    onChange={e => setEditingValues({ ...editingValues, beschreibung: e.target.value })}
                    style={{ padding: '4px 8px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '14px' }}
                  />
                  <input
                    type="number"
                    step="0.01"
                    value={editingValues.menge ?? item.menge}
                    onChange={e => setEditingValues({ ...editingValues, menge: parseFloat(e.target.value) || 0 })}
                    style={{ padding: '4px 8px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '14px', textAlign: 'right', width: '80px' }}
                  />
                  <input
                    type="number"
                    step="0.01"
                    value={editingValues.einzelpreis ?? item.einzelpreis}
                    onChange={e => setEditingValues({ ...editingValues, einzelpreis: parseFloat(e.target.value) || 0 })}
                    style={{ padding: '4px 8px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '14px', textAlign: 'right', width: '80px' }}
                  />
                  <span style={{ textAlign: 'right', fontWeight: '500' }}>
                    {formatCurrency((editingValues.menge ?? item.menge) * (editingValues.einzelpreis ?? item.einzelpreis))}
                  </span>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button onClick={() => handleSaveEdit(item.id)}
                      style={{ padding: '2px', background: 'none', border: 'none', cursor: 'pointer', color: '#10B981' }}>
                      <Check size={16} />
                    </button>
                    <button onClick={() => { setEditingItemId(null); setEditingValues({}); }}
                      style={{ padding: '2px', background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280' }}>
                      <X size={16} />
                    </button>
                  </div>
                </>
              ) : (
                // Anzeige-Modus
                <>
                  <span>{item.position}.</span>
                  <span style={{ fontWeight: '500' }}>{item.beschreibung}</span>
                  <span style={{ textAlign: 'right' }}>{item.menge.toFixed(2)} {item.einheit}</span>
                  <span style={{ textAlign: 'right' }}>{formatCurrency(item.einzelpreis)}</span>
                  <span style={{ textAlign: 'right', fontWeight: '500' }}>{formatCurrency(item.gesamtpreis)}</span>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {isEditable && (
                      <>
                        <button onClick={() => { setEditingItemId(item.id); setEditingValues({}); }}
                          style={{ padding: '2px', background: 'none', border: 'none', cursor: 'pointer', color: '#3B82F6' }}>
                          <Edit3 size={14} />
                        </button>
                        <button onClick={() => handleDeleteItem(item.id)}
                          style={{ padding: '2px', background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444' }}>
                          <Trash2 size={14} />
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}

          {/* Neue Position hinzufügen */}
          {showAddItem && (
            <div style={{
              display: 'grid', gridTemplateColumns: '40px 1fr 100px 100px 120px 60px',
              padding: '10px 16px', borderBottom: '1px solid #F3F4F6', alignItems: 'center',
              backgroundColor: '#F0FDF4'
            }}>
              <span style={{ color: '#6B7280', fontSize: '14px' }}>Neu</span>
              <input
                placeholder="Beschreibung"
                value={newItem.beschreibung}
                onChange={e => setNewItem({ ...newItem, beschreibung: e.target.value })}
                style={{ padding: '4px 8px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '14px' }}
              />
              <input
                type="number" step="0.01" placeholder="Menge"
                value={newItem.menge || ''}
                onChange={e => setNewItem({ ...newItem, menge: parseFloat(e.target.value) || 0 })}
                style={{ padding: '4px 8px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '14px', textAlign: 'right', width: '80px' }}
              />
              <input
                type="number" step="0.01" placeholder="Preis"
                value={newItem.einzelpreis || ''}
                onChange={e => setNewItem({ ...newItem, einzelpreis: parseFloat(e.target.value) || 0 })}
                style={{ padding: '4px 8px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '14px', textAlign: 'right', width: '80px' }}
              />
              <span style={{ textAlign: 'right', fontSize: '14px', fontWeight: '500' }}>
                {formatCurrency(newItem.menge * newItem.einzelpreis)}
              </span>
              <div style={{ display: 'flex', gap: '4px' }}>
                <button onClick={handleAddItem}
                  style={{ padding: '2px', background: 'none', border: 'none', cursor: 'pointer', color: '#10B981' }}>
                  <Check size={16} />
                </button>
                <button onClick={() => setShowAddItem(false)}
                  style={{ padding: '2px', background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280' }}>
                  <X size={16} />
                </button>
              </div>
            </div>
          )}

          {/* Summen */}
          <div style={{ padding: '12px 16px', borderTop: '2px solid #E5E7EB' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: '4px', maxWidth: '300px', marginLeft: 'auto' }}>
              <span style={{ fontSize: '14px', color: '#6B7280', textAlign: 'right' }}>Gesamtbetrag netto</span>
              <span style={{ fontSize: '14px', textAlign: 'right' }}>{formatCurrency(inv.netto_betrag)}</span>
              <span style={{ fontSize: '14px', color: '#6B7280', textAlign: 'right' }}>Umsatzsteuer {inv.ust_satz}%</span>
              <span style={{ fontSize: '14px', textAlign: 'right' }}>{formatCurrency(inv.ust_betrag)}</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#111827', textAlign: 'right', paddingTop: '4px', borderTop: '1px solid #E5E7EB' }}>
                Gesamtbetrag brutto
              </span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#111827', textAlign: 'right', paddingTop: '4px', borderTop: '1px solid #E5E7EB' }}>
                {formatCurrency(inv.brutto_betrag)}
              </span>
            </div>
          </div>
        </div>

        {/* Manueller Übernahme-Block: verbliebene offene Zusatzleistungen
            (z.B. ohne hinterlegten Stundensatz). Jederzeit übernehmbar. */}
        {isEditable && detailOpenZl.length > 0 && (
          <div style={{
            backgroundColor: 'white', borderRadius: '8px', border: '1px solid #E5E7EB',
            overflow: 'hidden', marginTop: '16px'
          }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '12px 16px', backgroundColor: '#FFFBEB', borderBottom: '1px solid #FDE68A'
            }}>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#B45309', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <AlertTriangle size={14} /> Offene Zusatzleistungen
              </span>
              <button
                onClick={detailConvertAll}
                disabled={detailZlConverting !== null}
                style={{
                  padding: '4px 10px', backgroundColor: '#EFF6FF', color: '#2563EB',
                  border: '1px solid #BFDBFE', borderRadius: '6px',
                  cursor: detailZlConverting !== null ? 'not-allowed' : 'pointer',
                  fontSize: '13px', fontWeight: '500'
                }}
              >
                Alle übernehmen
              </button>
            </div>
            {detailOpenZl.map(item => (
              <div key={item.id} style={{
                display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '12px', alignItems: 'center',
                padding: '10px 16px', borderBottom: '1px solid #F3F4F6'
              }}>
                <div>
                  <div style={{ fontSize: '13px', color: '#111827', whiteSpace: 'pre-wrap' }}>{item.beschreibung}</div>
                  <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>
                    {item.art === 'stunden' && item.stunden !== null
                      ? `${String(Number(item.stunden)).replace('.', ',')} Std${item.stundensatz !== null ? ` à ${formatCurrency(Number(item.stundensatz))}` : ''}`
                      : 'Festbetrag'}
                  </div>
                </div>
                <div style={{
                  fontSize: '13px', fontWeight: '600', textAlign: 'right', whiteSpace: 'nowrap',
                  color: Number(item.betrag) < 0 ? '#DC2626' : '#111827'
                }}>
                  {formatCurrency(Number(item.betrag))}
                </div>
                <button
                  onClick={() => detailConvertOne(item)}
                  disabled={detailZlConverting !== null}
                  style={{
                    padding: '4px 10px', backgroundColor: '#F0FDF4', color: '#16A34A',
                    border: '1px solid #BBF7D0', borderRadius: '6px',
                    cursor: detailZlConverting !== null ? 'not-allowed' : 'pointer',
                    fontSize: '13px', fontWeight: '500', whiteSpace: 'nowrap'
                  }}
                >
                  {detailZlConverting === item.id ? 'Übernehme...' : 'In Rechnung übernehmen'}
                </button>
              </div>
            ))}
          </div>
        )}

        {/* ============================================ */}
        {/* LETZTE RECHNUNG AN DIESEN KUNDEN             */}
        {/* Nur im Entwurfsstatus sichtbar               */}
        {/* ============================================ */}
        {isEditable && (
          <div style={{
            backgroundColor: 'white', borderRadius: '8px', border: '1px solid #E5E7EB',
            overflow: 'hidden', marginTop: '16px'
          }}>
            <button
              type="button"
              onClick={handleTogglePreviousInvoice}
              style={{
                width: '100%',
                padding: '12px 16px', backgroundColor: '#F9FAFB',
                borderBottom: showPreviousInvoice ? '1px solid #E5E7EB' : 'none',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                cursor: 'pointer', border: 'none', textAlign: 'left'
              }}
            >
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                Letzte Rechnung an diesen Kunden
              </span>
              <ChevronDown
                size={16}
                style={{
                  color: '#6B7280',
                  transform: showPreviousInvoice ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.15s'
                }}
              />
            </button>

            {showPreviousInvoice && (
              <div>
                {previousInvoiceLoading && (
                  <div style={{ padding: '20px 16px', fontSize: '14px', color: '#6B7280', textAlign: 'center' }}>
                    Wird geladen...
                  </div>
                )}

                {!previousInvoiceLoading && previousInvoiceLoaded && !previousInvoice && (
                  <div style={{ padding: '20px 16px', fontSize: '14px', color: '#6B7280', textAlign: 'center' }}>
                    Keine vorherige Rechnung für diesen Kunden vorhanden.
                  </div>
                )}

                {!previousInvoiceLoading && previousInvoice && (
                  <div>
                    {/* Header-Daten der Vorgängerrechnung */}
                    <div style={{ padding: '16px', borderBottom: '1px solid #E5E7EB', backgroundColor: '#FAFAFA' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 16px', fontSize: '14px', color: '#111827' }}>
                        {previousInvoice.rechnungsnummer && (
                          <><span style={{ color: '#6B7280' }}>Rechnungs-Nr.</span><span style={{ fontWeight: '500' }}>{previousInvoice.rechnungsnummer}</span></>
                        )}
                        <span style={{ color: '#6B7280' }}>Rechnungsdatum</span>
                        <span>{previousInvoice.rechnungsdatum ? formatDate(previousInvoice.rechnungsdatum) : '–'}</span>
                        <span style={{ color: '#6B7280' }}>Leistungszeitraum</span>
                        <span>{formatDate(previousInvoice.leistungszeitraum_von)} – {formatDate(previousInvoice.leistungszeitraum_bis)}</span>
                        <span style={{ color: '#6B7280' }}>Abrechnungsmonat</span>
                        <span>{GERMAN_MONTHS[previousInvoice.month]} {previousInvoice.year}</span>
                        <span style={{ color: '#6B7280' }}>Status</span>
                        <span>{STATUS_CONFIG[previousInvoice.status]?.label || previousInvoice.status}</span>
                      </div>
                      <div style={{ marginTop: '12px' }}>
                        <button
                          type="button"
                          onClick={() => handleDownloadPreviousInvoicePdf(previousInvoice.id)}
                          disabled={previousInvoicePdfLoading}
                          style={{
                            padding: '6px 12px', backgroundColor: '#F3F4F6', color: '#374151',
                            border: '1px solid #D1D5DB', borderRadius: '6px',
                            cursor: previousInvoicePdfLoading ? 'not-allowed' : 'pointer',
                            fontSize: '13px', fontWeight: '500', display: 'inline-flex', alignItems: 'center', gap: '6px',
                            opacity: previousInvoicePdfLoading ? 0.6 : 1
                          }}
                        >
                          <Download size={14} />
                          {previousInvoicePdfLoading ? 'Wird generiert...' : 'PDF anzeigen'}
                        </button>
                      </div>
                    </div>

                    {/* Positionstabelle der Vorgängerrechnung (readonly) */}
                    <div style={{
                      display: 'grid', gridTemplateColumns: '40px 1fr 100px 100px 120px',
                      padding: '8px 16px', fontSize: '12px', fontWeight: '600', color: '#6B7280',
                      borderBottom: '1px solid #E5E7EB', backgroundColor: '#FAFAFA'
                    }}>
                      <span>Pos.</span>
                      <span>Beschreibung</span>
                      <span style={{ textAlign: 'right' }}>Menge</span>
                      <span style={{ textAlign: 'right' }}>Einzelpreis</span>
                      <span style={{ textAlign: 'right' }}>Gesamtpreis</span>
                    </div>

                    {(previousInvoice.line_items || []).slice().sort((a, b) => a.position - b.position).map(item => (
                      <div key={item.id} style={{
                        display: 'grid', gridTemplateColumns: '40px 1fr 100px 100px 120px',
                        padding: '10px 16px', borderBottom: '1px solid #F3F4F6', alignItems: 'center',
                        fontSize: '14px', color: '#111827'
                      }}>
                        <span>{item.position}.</span>
                        <span>{item.beschreibung}</span>
                        <span style={{ textAlign: 'right' }}>{item.menge} {item.einheit}</span>
                        <span style={{ textAlign: 'right' }}>{formatCurrency(item.einzelpreis)}</span>
                        <span style={{ textAlign: 'right' }}>{formatCurrency(item.gesamtpreis)}</span>
                      </div>
                    ))}

                    {/* Summen der Vorgängerrechnung */}
                    <div style={{ padding: '12px 16px', borderTop: '2px solid #E5E7EB' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: '4px', maxWidth: '300px', marginLeft: 'auto' }}>
                        <span style={{ fontSize: '14px', color: '#6B7280', textAlign: 'right' }}>Gesamtbetrag netto</span>
                        <span style={{ fontSize: '14px', textAlign: 'right' }}>{formatCurrency(previousInvoice.netto_betrag)}</span>
                        <span style={{ fontSize: '14px', color: '#6B7280', textAlign: 'right' }}>Umsatzsteuer {previousInvoice.ust_satz}%</span>
                        <span style={{ fontSize: '14px', textAlign: 'right' }}>{formatCurrency(previousInvoice.ust_betrag)}</span>
                        <span style={{ fontSize: '14px', fontWeight: '600', color: '#111827', textAlign: 'right', paddingTop: '4px', borderTop: '1px solid #E5E7EB' }}>
                          Gesamtbetrag brutto
                        </span>
                        <span style={{ fontSize: '14px', fontWeight: '600', color: '#111827', textAlign: 'right', paddingTop: '4px', borderTop: '1px solid #E5E7EB' }}>
                          {formatCurrency(previousInvoice.brutto_betrag)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Drei-Wege-Dialog: Lohnjournal mit bestehenden Positionen */}
        {pendingPayrollDecision && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1100
          }}>
            <div style={{
              backgroundColor: 'white', borderRadius: '12px', padding: '24px',
              maxWidth: '520px', width: '90%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
            }}>
              <h2 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                Bestehende Positionen vorhanden
              </h2>
              <p style={{ fontSize: '14px', color: '#374151', lineHeight: '1.6', margin: '0 0 8px 0' }}>
                Diese Rechnung enthält bereits <strong>{pendingPayrollDecision.existingCount} Position(en)</strong>.
              </p>
              <p style={{ fontSize: '14px', color: '#374151', lineHeight: '1.6', margin: '0 0 20px 0' }}>
                Das Lohnjournal für <strong>{pendingPayrollDecision.customerCompany}</strong> ({GERMAN_MONTHS[pendingPayrollDecision.month]} {pendingPayrollDecision.year}) hat <strong>{pendingPayrollDecision.anzahlArbeitnehmer} Arbeitnehmer</strong>.
              </p>
              {pendingPayrollDecision.warnung && (
                <div style={{
                  padding: '10px 12px', marginBottom: '20px',
                  backgroundColor: '#FFFBEB', color: '#92400E',
                  border: '1px solid #FDE68A', borderRadius: '6px',
                  fontSize: '13px', lineHeight: '1.5'
                }}>
                  {pendingPayrollDecision.warnung}
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <button
                  onClick={() => handlePayrollDecision('append')}
                  disabled={invoicePayrollBusy}
                  style={{
                    padding: '10px 16px', backgroundColor: '#3B82F6', color: 'white',
                    border: 'none', borderRadius: '6px', cursor: invoicePayrollBusy ? 'not-allowed' : 'pointer',
                    fontSize: '14px', fontWeight: '500',
                    opacity: invoicePayrollBusy ? 0.6 : 1
                  }}
                >
                  Neue Buchungen zusätzlich übernehmen
                </button>
                <button
                  onClick={() => handlePayrollDecision('replace')}
                  disabled={invoicePayrollBusy}
                  style={{
                    padding: '10px 16px', backgroundColor: '#F59E0B', color: 'white',
                    border: 'none', borderRadius: '6px', cursor: invoicePayrollBusy ? 'not-allowed' : 'pointer',
                    fontSize: '14px', fontWeight: '500',
                    opacity: invoicePayrollBusy ? 0.6 : 1
                  }}
                >
                  Alte Buchungen durch neue ersetzen
                </button>
                <button
                  onClick={handlePayrollDecisionCancel}
                  disabled={invoicePayrollBusy}
                  style={{
                    padding: '10px 16px', backgroundColor: '#F3F4F6', color: '#374151',
                    border: '1px solid #D1D5DB', borderRadius: '6px',
                    cursor: invoicePayrollBusy ? 'not-allowed' : 'pointer',
                    fontSize: '14px', fontWeight: '500'
                  }}
                >
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        )}
        {zlPromptModal}
      </div>
    );
  }

  // Sammelaktion: Beschriftung und Sperre des Knopfes. Während der Vor-Prüfung
  // und des Versands zeigt der Knopf den Bearbeitungsstand, damit nie der
  // Eindruck entsteht, der Klick sei wirkungslos geblieben.
  const bulkAktiv = bulkSending || bulkPhase !== null;
  const bulkButtonLabel = bulkPhase
    ? (bulkPhase.art === 'pruefung'
        ? `Prüfe ${bulkPhase.aktuell} von ${bulkPhase.gesamt}...`
        : `Versende ${bulkPhase.aktuell} von ${bulkPhase.gesamt}...`)
    : (bulkSending ? 'Wird versendet...' : `Markierte freigeben & versenden (${selectedIds.length})`);

  // ============================================
  // LIST VIEW
  // ============================================
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#111827', margin: 0 }}>
          Rechnungen
        </h1>
        {activeView === 'rechnungen' && (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', position: 'relative' }}>
          <button
            onClick={() => setShowDatevExport(!showDatevExport)}
            style={{
              padding: '8px 16px',
              backgroundColor: showDatevExport ? '#F0FDF4' : '#F9FAFB',
              color: showDatevExport ? '#15803D' : '#374151',
              border: `1px solid ${showDatevExport ? '#86EFAC' : '#D1D5DB'}`,
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <Download size={16} />
            DATEV Export
          </button>
          <button
            onClick={() => setShowSepaExport(!showSepaExport)}
            style={{
              padding: '8px 16px',
              backgroundColor: showSepaExport ? '#EEF2FF' : '#F9FAFB',
              color: showSepaExport ? '#4338CA' : '#374151',
              border: `1px solid ${showSepaExport ? '#C7D2FE' : '#D1D5DB'}`,
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <Download size={16} />
            SEPA-Lastschrift
          </button>
          <button
            onClick={() => setShowUploadModal(true)}
            style={{
              padding: '8px 16px',
              backgroundColor: '#EFF6FF',
              color: '#2563EB',
              border: '1px solid #BFDBFE',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <Upload size={16} />
            Lohnjournal hochladen
          </button>
          <button
            onClick={() => navigate('/rechnungen/datev-weiterberechnung')}
            style={{
              padding: '8px 16px',
              backgroundColor: '#FAF5FF',
              color: '#7E22CE',
              border: '1px solid #E9D5FF',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <FileInput size={16} />
            DATEV-Weiterberechnung
          </button>
          {/* DATEV Export Picker */}
          {showDatevExport && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: '8px',
              padding: '16px',
              backgroundColor: 'white',
              borderRadius: '8px',
              border: '1px solid #E5E7EB',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              zIndex: 10,
              minWidth: '280px'
            }}>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '4px' }}>
                DATEV Buchungsstapel – bis einschließlich
              </div>
              <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '12px', lineHeight: '1.4' }}>
                Exportiert alle noch nicht exportierten, freigegebenen/versendeten Rechnungen
                bis zum gewählten Monat (monatsübergreifend). Buchhaltungsmonat in DATEV
                bleibt der gewählte Monat.
              </div>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                <select
                  value={datevMonth}
                  onChange={(e) => setDatevMonth(Number(e.target.value))}
                  style={{
                    flex: 1, padding: '6px 10px', border: '1px solid #D1D5DB',
                    borderRadius: '6px', fontSize: '14px', color: '#111827'
                  }}
                >
                  {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                    <option key={m} value={m}>{GERMAN_MONTHS[m]}</option>
                  ))}
                </select>
                <select
                  value={datevYear}
                  onChange={(e) => setDatevYear(Number(e.target.value))}
                  style={{
                    width: '90px', padding: '6px 10px', border: '1px solid #D1D5DB',
                    borderRadius: '6px', fontSize: '14px', color: '#111827'
                  }}
                >
                  {[2025, 2026, 2027].map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <button
                onClick={handleDatevExport}
                disabled={datevExporting}
                style={{
                  width: '100%', padding: '8px 16px',
                  backgroundColor: datevExporting ? '#9CA3AF' : '#15803D',
                  color: 'white', border: 'none', borderRadius: '6px',
                  cursor: datevExporting ? 'not-allowed' : 'pointer',
                  fontSize: '14px', fontWeight: '500'
                }}
              >
                {datevExporting ? 'Wird exportiert...' : 'EXTF-Datei herunterladen'}
              </button>
            </div>
          )}
          {/* SEPA-Lastschrift Export Picker */}
          {showSepaExport && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: '8px',
              padding: '16px',
              backgroundColor: 'white',
              borderRadius: '8px',
              border: '1px solid #E5E7EB',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              zIndex: 10,
              minWidth: '320px'
            }}>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '4px' }}>
                SEPA-Lastschrift exportieren
              </div>
              <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '12px', lineHeight: '1.4' }}>
                Zieht alle offenen, noch nicht exportierten Lastschrift-Rechnungen bis
                einschließlich des gewählten Monats (monatsübergreifend). Bereits bezahlte
                Rechnungen werden nicht eingezogen.
              </div>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                <select
                  value={sepaMonth}
                  onChange={(e) => setSepaMonth(Number(e.target.value))}
                  style={{
                    flex: 1, padding: '6px 10px', border: '1px solid #D1D5DB',
                    borderRadius: '6px', fontSize: '14px', color: '#111827'
                  }}
                >
                  {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                    <option key={m} value={m}>{GERMAN_MONTHS[m]}</option>
                  ))}
                </select>
                <select
                  value={sepaYear}
                  onChange={(e) => setSepaYear(Number(e.target.value))}
                  style={{
                    width: '90px', padding: '6px 10px', border: '1px solid #D1D5DB',
                    borderRadius: '6px', fontSize: '14px', color: '#111827'
                  }}
                >
                  {[2025, 2026, 2027].map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '12px', color: '#6B7280', display: 'block', marginBottom: '4px' }}>
                  Einzugsdatum (Default: +3 Bankarbeitstage)
                </label>
                <input
                  type="date"
                  value={sepaReqdColltnDt}
                  onChange={(e) => setSepaReqdColltnDt(e.target.value)}
                  style={{
                    width: '100%', padding: '6px 10px', border: '1px solid #D1D5DB',
                    borderRadius: '6px', fontSize: '14px', color: '#111827',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <button
                onClick={handleSepaExport}
                disabled={sepaExporting}
                style={{
                  width: '100%', padding: '8px 16px',
                  backgroundColor: sepaExporting ? '#9CA3AF' : '#4338CA',
                  color: 'white', border: 'none', borderRadius: '6px',
                  cursor: sepaExporting ? 'not-allowed' : 'pointer',
                  fontSize: '14px', fontWeight: '500'
                }}
              >
                {sepaExporting ? 'Wird exportiert...' : 'SEPA-XML herunterladen'}
              </button>
            </div>
          )}
        </div>
        )}
      </div>

      {/* View-Switcher: Rechnungen verwalten vs. Zahlungsabgleich */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        <button
          onClick={() => handleViewChange('rechnungen')}
          style={{
            padding: '8px 16px',
            backgroundColor: activeView === 'rechnungen' ? '#EFF6FF' : '#F9FAFB',
            color: activeView === 'rechnungen' ? '#2563EB' : '#6B7280',
            border: `1px solid ${activeView === 'rechnungen' ? '#BFDBFE' : '#E5E7EB'}`,
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: activeView === 'rechnungen' ? '600' : '500'
          }}
        >
          Rechnungen verwalten
        </button>
        <button
          onClick={() => handleViewChange('zahlungsabgleich')}
          style={{
            padding: '8px 16px',
            backgroundColor: activeView === 'zahlungsabgleich' ? '#EFF6FF' : '#F9FAFB',
            color: activeView === 'zahlungsabgleich' ? '#2563EB' : '#6B7280',
            border: `1px solid ${activeView === 'zahlungsabgleich' ? '#BFDBFE' : '#E5E7EB'}`,
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: activeView === 'zahlungsabgleich' ? '600' : '500'
          }}
        >
          Zahlungsabgleich
        </button>
        <button
          onClick={() => handleViewChange('zusatzleistungen')}
          style={{
            padding: '8px 16px',
            backgroundColor: activeView === 'zusatzleistungen' ? '#EFF6FF' : '#F9FAFB',
            color: activeView === 'zusatzleistungen' ? '#2563EB' : '#6B7280',
            border: `1px solid ${activeView === 'zusatzleistungen' ? '#BFDBFE' : '#E5E7EB'}`,
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: activeView === 'zusatzleistungen' ? '600' : '500'
          }}
        >
          Zusatzleistungen
        </button>
      </div>

      {activeView === 'zahlungsabgleich' ? (
        <PaymentReconciliationView />
      ) : activeView === 'zusatzleistungen' ? (
        <ZusatzleistungenView />
      ) : (
        <>

      {/* Monat-Picker */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
        <button
          onClick={() => navigateMonth(-1)}
          disabled={isAlleMonate}
          style={{
            padding: '6px 10px',
            backgroundColor: isAlleMonate ? '#F3F4F6' : '#F9FAFB',
            color: isAlleMonate ? '#9CA3AF' : '#374151',
            border: '1px solid #E5E7EB',
            borderRadius: '6px',
            cursor: isAlleMonate ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <ChevronLeft size={16} />
        </button>

        <div style={{
          minWidth: '160px',
          textAlign: 'center',
          padding: '6px 14px',
          backgroundColor: '#FFFFFF',
          border: '1px solid #E5E7EB',
          borderRadius: '6px',
          fontSize: '14px',
          fontWeight: '500',
          color: '#111827'
        }}>
          {isAlleMonate
            ? 'Alle Monate'
            : `${GERMAN_MONTHS[filter.month!]} ${filter.year}`}
        </div>

        <button
          onClick={() => navigateMonth(1)}
          disabled={isAlleMonate}
          style={{
            padding: '6px 10px',
            backgroundColor: isAlleMonate ? '#F3F4F6' : '#F9FAFB',
            color: isAlleMonate ? '#9CA3AF' : '#374151',
            border: '1px solid #E5E7EB',
            borderRadius: '6px',
            cursor: isAlleMonate ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <ChevronRight size={16} />
        </button>

        <button
          onClick={goToVormonat}
          disabled={isAlleMonate}
          style={{
            padding: '6px 14px',
            backgroundColor: isAlleMonate ? '#F3F4F6' : '#F9FAFB',
            color: isAlleMonate ? '#9CA3AF' : '#374151',
            border: '1px solid #E5E7EB',
            borderRadius: '6px',
            cursor: isAlleMonate ? 'not-allowed' : 'pointer',
            fontSize: '13px',
            fontWeight: '500'
          }}
        >
          Heute
        </button>

        <button
          onClick={toggleAlleMonate}
          style={{
            padding: '6px 14px',
            backgroundColor: isAlleMonate ? '#EFF6FF' : '#F9FAFB',
            color: isAlleMonate ? '#2563EB' : '#374151',
            border: `1px solid ${isAlleMonate ? '#BFDBFE' : '#E5E7EB'}`,
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: isAlleMonate ? '600' : '500'
          }}
        >
          Alle Monate
        </button>
      </div>

      {/* Filter Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        {[
          { key: undefined, label: 'Alle', count: counts.alle },
          { key: 'entwurf' as const, label: 'Entwürfe', count: counts.entwurf },
          { key: 'freigegeben' as const, label: 'Freigegeben', count: counts.freigegeben },
          { key: 'versendet' as const, label: 'Versendet', count: counts.versendet }
        ].map(tab => (
          <button
            key={tab.label}
            onClick={() => setFilter({ ...filter, status: tab.key })}
            style={{
              padding: '6px 14px',
              backgroundColor: filter.status === tab.key ? '#EFF6FF' : '#F9FAFB',
              color: filter.status === tab.key ? '#2563EB' : '#6B7280',
              border: `1px solid ${filter.status === tab.key ? '#BFDBFE' : '#E5E7EB'}`,
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: filter.status === tab.key ? '600' : '400',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            {tab.label}
            <span style={{
              backgroundColor: filter.status === tab.key ? '#BFDBFE' : '#E5E7EB',
              padding: '1px 6px',
              borderRadius: '10px',
              fontSize: '11px',
              fontWeight: '600'
            }}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Bulk-Versand-Balken: nur im Entwürfe-Tab und nur bei aktiver Auswahl */}
      {isEntwurfTab && selectedIds.length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px',
          padding: '10px 16px', backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE',
          borderRadius: '8px'
        }}>
          <span style={{ fontSize: '14px', fontWeight: '600', color: '#1E40AF' }}>
            {selectedIds.length} ausgewählt
          </span>
          <button
            onClick={() => setSelectedIds(filteredInvoices.map(i => i.id))}
            disabled={bulkAktiv}
            style={{
              padding: '4px 10px', backgroundColor: 'transparent', color: '#2563EB',
              border: 'none', borderRadius: '6px',
              cursor: bulkAktiv ? 'not-allowed' : 'pointer',
              fontSize: '13px', fontWeight: '500'
            }}
          >
            Alle auswählen
          </button>
          <button
            onClick={() => setSelectedIds([])}
            disabled={bulkAktiv}
            style={{
              padding: '4px 10px', backgroundColor: 'transparent', color: '#6B7280',
              border: 'none', borderRadius: '6px',
              cursor: bulkAktiv ? 'not-allowed' : 'pointer',
              fontSize: '13px', fontWeight: '500'
            }}
          >
            Auswahl aufheben
          </button>
          <div style={{ marginLeft: 'auto' }}>
            <button
              onClick={handleBulkApproveAndSend}
              disabled={bulkAktiv}
              style={{
                padding: '8px 16px',
                backgroundColor: bulkAktiv ? '#9CA3AF' : '#3B82F6',
                color: 'white', border: 'none', borderRadius: '6px',
                cursor: bulkAktiv ? 'not-allowed' : 'pointer',
                fontSize: '14px', fontWeight: '500',
                display: 'flex', alignItems: 'center', gap: '6px'
              }}
            >
              <Send size={16} />
              {bulkButtonLabel}
            </button>
          </div>
        </div>
      )}

      {/* Rechnungsliste */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
          Lade Rechnungen...
        </div>
      ) : filteredInvoices.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280', backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
          <FileText size={40} color="#D1D5DB" style={{ margin: '0 auto 12px' }} />
          <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '4px' }}>Keine Rechnungen gefunden</div>
          <div style={{ fontSize: '14px' }}>
            {filter.status === 'entwurf' 
              ? 'Rechnungsentwürfe werden automatisch nach der Finalisierung erstellt.'
              : 'Für den gewählten Filter gibt es keine Rechnungen.'}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {filteredInvoices.map(inv => {
            const statusCfg = STATUS_CONFIG[inv.status];
            const listEmailEmpfaenger = inv.customer?.rechnungs_email || inv.customer?.email || null;
            const istEntwurfMitLA = hatLohnabrechnungen(inv);
            const badgeLabel = istEntwurfMitLA ? 'Entwurf mit LA' : statusCfg.label;
            const badgeBg = istEntwurfMitLA ? '#ECFDF5' : statusCfg.bg;
            const badgeColor = istEntwurfMitLA ? '#059669' : statusCfg.color;
            const badgeBorder = istEntwurfMitLA ? '#A7F3D0' : statusCfg.border;
            return (
              <div
                key={inv.id}
                onClick={() => handleOpenInvoice(inv)}
                style={{
                  padding: '16px',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  border: '1px solid #E5E7EB',
                  cursor: 'pointer',
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                  display: 'grid',
                  gridTemplateColumns: isEntwurfTab ? '32px 1fr auto auto auto' : '1fr auto auto auto',
                  alignItems: 'center',
                  gap: '16px'
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = '#BFDBFE';
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = '#E5E7EB';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {isEntwurfTab && (
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(inv.id)}
                      onChange={() => toggleSelect(inv.id)}
                      onClick={(e) => e.stopPropagation()}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                  </div>
                )}
                <div>
                  <div style={{ fontSize: '15px', fontWeight: '600', color: '#111827' }}>
                    {inv.customer?.company || inv.empfaenger_firma}
                  </div>
                  <div style={{ fontSize: '13px', color: '#6B7280', marginTop: '2px' }}>
                    {GERMAN_MONTHS[inv.month]} {inv.year}
                    {inv.rechnungsnummer && ` · ${inv.rechnungsnummer}`}
                  </div>
                  {listEmailEmpfaenger && (
                    <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '2px' }}>
                      → {listEmailEmpfaenger}
                    </div>
                  )}
                  {inv.status === 'entwurf' && openZlCounts[inv.id] > 0 && (
                    <div style={{
                      display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '6px',
                      padding: '2px 8px', borderRadius: '10px', fontSize: '12px', fontWeight: '500',
                      backgroundColor: '#FFFBEB', color: '#B45309', border: '1px solid #FDE68A'
                    }}>
                      <AlertTriangle size={12} />
                      {openZlCounts[inv.id]} offene {openZlCounts[inv.id] === 1 ? 'Zusatzleistung' : 'Zusatzleistungen'}
                    </div>
                  )}
                </div>

                <span style={{
                  padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500',
                  backgroundColor: badgeBg, color: badgeColor, border: `1px solid ${badgeBorder}`
                }}>
                  {badgeLabel}
                </span>

                <div style={{ textAlign: 'right', minWidth: '100px' }}>
                  <div style={{ fontSize: '15px', fontWeight: '600', color: '#111827' }}>
                    {formatCurrency(inv.brutto_betrag)}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6B7280' }}>
                    brutto
                  </div>
                </div>

                {inv.status === 'entwurf' && (
                  <SplitApproveButton
                    primaryLabel="Freigeben & Versenden"
                    primaryLabelBusy="Wird versendet..."
                    secondaryLabel="Freigeben & Herunterladen"
                    secondaryLabelBusy="Wird generiert..."
                    onPrimary={() => handleApprove(inv.id)}
                    onSecondary={() => handleApproveAndDownload(inv.id)}
                    busyPrimary={approving === inv.id}
                    busySecondary={approvingDownload === inv.id}
                    size="sm"
                    stopPropagation
                  />
                )}
                {inv.status === 'freigegeben' && (
                  <SplitApproveButton
                    primaryLabel="Versenden"
                    primaryLabelBusy="Wird versendet..."
                    secondaryLabel="Herunterladen"
                    secondaryLabelBusy="Wird generiert..."
                    onPrimary={() => handleApprove(inv.id)}
                    onSecondary={() => handleApproveAndDownload(inv.id)}
                    busyPrimary={approving === inv.id}
                    busySecondary={approvingDownload === inv.id}
                    size="sm"
                    stopPropagation
                  />
                )}
                {inv.status === 'versendet' && inv.versendet_an && (
                  <div style={{
                    padding: '6px 12px', backgroundColor: '#ECFDF5', color: '#059669',
                    borderRadius: '6px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px',
                    border: '1px solid #A7F3D0', whiteSpace: 'nowrap'
                  }}>
                    <Check size={14} /> Versendet
                  </div>
                )}
                {inv.status === 'versendet' && !inv.versendet_an && <div style={{ width: '100px' }} />}
              </div>
            );
          })}
        </div>
      )}

        </>
      )}

      {/* Lohnjournal Upload Modal */}
      {showUploadModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', padding: '24px',
            maxWidth: '520px', width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                Lohnjournal hochladen
              </h2>
              <button
                onClick={resetUploadModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
              >
                <X size={20} color="#6B7280" />
              </button>
            </div>

            {uploadStatus === 'idle' && (
              <>
                <div style={{
                  padding: '12px 16px', backgroundColor: '#EFF6FF', borderRadius: '6px',
                  border: '1px solid #BFDBFE', marginBottom: '20px'
                }}>
                  <p style={{ fontSize: '13px', color: '#1E40AF', margin: 0, lineHeight: '1.5' }}>
                    <strong>Automatische Erkennung:</strong> Beraternummer, Mandantennummer, Abrechnungsmonat 
                    und Arbeitnehmeranzahl werden direkt aus dem DATEV-Lohnjournal extrahiert. Es können mehrere 
                    Dateien gleichzeitig ausgewählt werden, sie werden nacheinander verarbeitet.
                  </p>
                </div>

                <div
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = '#3B82F6'; }}
                  onDragLeave={(e) => { e.currentTarget.style.borderColor = '#D1D5DB'; }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.style.borderColor = '#D1D5DB';
                    const dateien = sammleDateien(e.dataTransfer.files, true);
                    if (dateien.length > 0) {
                      handleLohnjournalUpload(dateien);
                    }
                  }}
                  style={{
                    border: '2px dashed #D1D5DB', borderRadius: '8px', padding: '40px 24px',
                    textAlign: 'center', cursor: 'pointer', transition: 'border-color 0.2s'
                  }}
                >
                  <Upload size={32} color="#9CA3AF" style={{ margin: '0 auto 12px' }} />
                  <div style={{ fontSize: '15px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                    DATEV-Lohnjournale auswählen
                  </div>
                  <div style={{ fontSize: '13px', color: '#6B7280' }}>
                    Eine oder mehrere PDF-Dateien hierhin ziehen oder klicken
                  </div>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  multiple
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const dateien = sammleDateien(e.target.files, false);
                    if (dateien.length > 0) {
                      handleLohnjournalUpload(dateien);
                    }
                  }}
                />
              </>
            )}

            {uploadStatus === 'laeuft' && (
              <div style={{ padding: '8px 0' }}>
                <div style={{ textAlign: 'center', padding: '8px 0 20px' }}>
                  <div style={{
                    width: '40px', height: '40px', borderRadius: '50%',
                    border: '3px solid #E5E7EB', borderTopColor: '#3B82F6',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 16px'
                  }} />
                  {uploadFortschritt && (
                    <>
                      <div style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>
                        Datei {uploadFortschritt.aktuelleDatei} von {uploadFortschritt.gesamtDateien}
                      </div>
                      <div style={{ fontSize: '13px', color: '#6B7280', marginTop: '4px', wordBreak: 'break-word' }}>
                        {uploadFortschritt.dateiname}
                      </div>
                      <div style={{ fontSize: '13px', color: '#6B7280', marginTop: '4px' }}>
                        {uploadFortschritt.phase}
                      </div>
                    </>
                  )}
                  <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                </div>

                {uploadErgebnisse.length > 0 && (
                  <div style={{ maxHeight: '260px', overflowY: 'auto', paddingRight: '4px' }}>
                    {uploadErgebnisse.map((ergebnis, index) => renderImportErgebnis(ergebnis, index))}
                  </div>
                )}
              </div>
            )}

            {uploadStatus === 'fertig' && (() => {
              const bilanz = lohnjournalImportService.bilanz(uploadErgebnisse);
              const alleErfolgreich = bilanz.fehler === 0 && bilanz.warnung === 0;
              const kopfConfig = bilanz.fehler > 0
                ? IMPORT_STATUS_CONFIG.fehler
                : (bilanz.warnung > 0 ? IMPORT_STATUS_CONFIG.warnung : IMPORT_STATUS_CONFIG.erfolg);

              const teile: string[] = [];
              if (bilanz.erfolg > 0) teile.push(`${bilanz.erfolg} erfolgreich`);
              if (bilanz.warnung > 0) teile.push(`${bilanz.warnung} mit Warnung`);
              if (bilanz.fehler > 0) teile.push(`${bilanz.fehler} fehlgeschlagen`);

              return (
                <div style={{ padding: '8px 0' }}>
                  <div style={{
                    padding: '12px 16px', backgroundColor: kopfConfig.bg, borderRadius: '6px',
                    border: `1px solid ${kopfConfig.border}`, marginBottom: '16px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      {alleErfolgreich
                        ? <Check size={18} color={kopfConfig.color} />
                        : <AlertTriangle size={18} color={kopfConfig.color} />}
                      <span style={{ fontSize: '14px', fontWeight: '600', color: kopfConfig.color }}>
                        {bilanz.gesamt === 1 ? '1 Datei verarbeitet' : `${bilanz.gesamt} Dateien verarbeitet`}
                      </span>
                    </div>
                    <p style={{ fontSize: '13px', color: '#374151', margin: 0, lineHeight: '1.5' }}>
                      {teile.join(', ')}
                    </p>
                  </div>

                  <div style={{ maxHeight: '320px', overflowY: 'auto', paddingRight: '4px', marginBottom: '16px' }}>
                    {uploadErgebnisse.map((ergebnis, index) => renderImportErgebnis(ergebnis, index))}
                  </div>

                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={starteWeiterenUpload}
                      style={{
                        flex: 1, padding: '10px', backgroundColor: '#3B82F6', color: 'white',
                        border: 'none', borderRadius: '6px', cursor: 'pointer',
                        fontSize: '14px', fontWeight: '500'
                      }}
                    >
                      Weitere hochladen
                    </button>
                    <button
                      onClick={resetUploadModal}
                      style={{
                        padding: '10px 16px', backgroundColor: '#F3F4F6', color: '#374151',
                        border: 'none', borderRadius: '6px', cursor: 'pointer',
                        fontSize: '14px', fontWeight: '500'
                      }}
                    >
                      Schließen
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}
      {zlPromptModal}
    </div>
  );
};
